import {
  SportState,
  Sport,
  SportsApiResponse,
  TournamentsApiResponse,
  FeaturedTournament,
  Category,
  CategoriesApiResponse,
  CategoriesBySport,
  Tournament,
  TournamentsForSportCategoryResponse,
  EventDetailsEvent,
  EventOddsMap,
  PreviewOdd,
  PreviewEvent,
  PreviewEventGroup,
  EventDetailsMarketsGroup,
  EventDetailsResponse,
  EventByID,
  MarketGroup,
  LiveEventDetailsResponse,
  LiveEventByID,
  LiveEventDetailsEvent,
  FieldStatisticsState,
  BetOutcomeModal,
  SuccessBetOutcomeModal,
  BookedBetOutcomeModal,
  ErrorBetOutcomeModal,
  ReviewBetOutcomeModal,
  OddsToUpdate
} from '../../../types/sport';
import {
  AddOddToCartV3Payload,
  AddOddEventV3,
  OddMarketIncoming,
  AddOddIncoming,
  BetEntityLoose
} from '../../../types/cart';
import { useApiV3 } from '../../composables/apiV3';
import store from '../index';

// Normalizzazione odds ed outcome in oggetti keyed da unique_id
type PreviewOutcomeNormalized =
  import('../../../types/sport').PreviewOutcome & {
    unique_id: string;
  };
type PreviewOddNormalized = Omit<
  import('../../../types/sport').PreviewOdd,
  'outcome'
> & {
  unique_id: string;
  outcome: Record<string, PreviewOutcomeNormalized>;
};
type LiveEventOddsNormalized = Record<string, PreviewOddNormalized>;

// Tipi per stats iframes
interface StatsIframeRule {
  type: string;
  scope: string;
  source: number;
  configuration: string;
  status: boolean;
}

interface StatsIframePlatform {
  platformId: number;
  rules: StatsIframeRule[];
}

interface StatsIframesResponse {
  platforms: StatsIframePlatform[];
}

// Tipi per gestione errori
export interface BetErrorResponse {
  error_code?: string;
  message?: string;
  reason?: string;
  result?: string;
  data_to_update?: any[];
}

export interface BetErrorState {
  response: BetErrorResponse;
  betType: 'place' | 'book';
  errorCode: string;
  errorReason: string;
}

export interface CartValidationError {
  message: string;
  value?: number;
}

function normalizePreviewOddsToObject(
  list: import('../../../types/sport').PreviewOdd[]
): LiveEventOddsNormalized {
  const result: LiveEventOddsNormalized = {};
  (list || []).forEach((o: any) => {
    const outcomesObj: Record<string, PreviewOutcomeNormalized> = {};
    (o.outcome || []).forEach((out: any) => {
      const key = String(
        out.unique_id || out.id_outcome || out.outcome_id || ''
      );
      outcomesObj[key] = {
        ...out,
        unique_id: String(
          out.unique_id || out.id_outcome || out.outcome_id || ''
        ),
        // Default live se non specificato (il normalizzatore è usato in contesti live)
        type: out && out.type ? out.type : 'live'
      } as PreviewOutcomeNormalized;
    });
    const key = String(o.unique_id || o.id);
    result[key] = {
      ...(o as any),
      unique_id: String(o.unique_id || o.id),
      outcome: outcomesObj,
      // Preserva mainLine se presente
      ...(o.mainLine != null && { mainLine: String(o.mainLine) })
    } as PreviewOddNormalized;
  });
  return result;
}

// Interfacce per aggiornare outcomes live
export interface LiveOddsSetAttributes {
  external_id?: string;
  status?: number;
  odds?: string;
  lastUpdate?: string;
}
export interface LiveOddsSetItem {
  _attributes: LiveOddsSetAttributes;
  unique_id: string; // es. "1799771885;6539517878#12.0;4058227956"
}
export interface LiveOddsUpdatePayloadItem {
  id: number | string; // eventId
  odds: {
    set: LiveOddsSetItem[];
  };
}

function extractMarketUniqueIdFromComposite(uniqueId: string): string | null {
  const parts = String(uniqueId || '').split(';');
  if (parts.length >= 3) return parts[1];
  return null;
}

// Helpers per marcare gli outcome con type 'prematch' | 'live'
function addTypeToPreviewOddList(
  list: import('../../../types/sport').PreviewOdd[] | undefined,
  type: 'prematch' | 'live'
): import('../../../types/sport').PreviewOdd[] {
  const source = Array.isArray(list) ? list : [];
  return source.map((odd: any) => ({
    ...odd,
    outcome: (Array.isArray(odd?.outcome) ? odd.outcome : []).map(
      (out: any) => ({
        ...out,
        type
      })
    )
  }));
}

function addTypeToEventOddsMap(
  map: EventOddsMap | undefined,
  type: 'prematch' | 'live'
): EventOddsMap {
  const input: EventOddsMap = (map as any) || ({} as any);
  const result: EventOddsMap = {} as any;
  Object.keys(input).forEach((marketKey: string) => {
    const list = (input as any)[
      marketKey
    ] as import('../../../types/sport').PreviewOdd[];
    (result as any)[marketKey] = addTypeToPreviewOddList(list, type);
  });
  return result;
}

function addTypeToPreviewOddArray(
  odds: import('../../../types/sport').PreviewOdd[] | undefined,
  type: 'prematch' | 'live'
): import('../../../types/sport').PreviewOdd[] {
  return addTypeToPreviewOddList(odds, type);
}

const state: SportState = {
  sports: [],
  sportsAntepost: [],
  sportLive: [],
  categories: {},
  tournamentsByCategory: {},
  featuredTournamnetsBySport: {},
  tournaments: [],
  eventsById: {},
  eventsByTournament: {},
  previewByTournament: {},
  headerSignByTournament: {},
  marketsByTournament: {},
  liveEventsBySport: {},
  liveSportsAvailable: [],
  liveEventById: {},
  fieldStatistics: { sportId: null, eventId: null } as FieldStatisticsState,
  activeLiveSport: null,
  loading: false,
  loadingLive: false,
  loadingTournaments: false,
  loadingCategories: false,
  error: null,
  tournamentsError: null,
  categoriesError: null,
  recentlyUpdatedOutcomes: {},
  // mappa { source -> rule } per stats iframes
  // @ts-ignore aggiunta dinamica su SportState
  statsIframesRulesMap: {} as Record<string, StatsIframeRule>,
  // flag per mostrare il messaggio temporaneo di aggiornamento quote
  // @ts-ignore aggiunta dinamica su SportState
  showOddsUpdateMessage: false as boolean,
  // @ts-ignore aggiunta dinamica su SportState
  betError: null as BetErrorState | null,
  // @ts-ignore aggiunta dinamica su SportState
  cartValidationErrors: [] as CartValidationError[],
  // Stato salvato per la navigazione back - Tournament
  // @ts-ignore aggiunta dinamica su SportState
  savedTournamentMarketState: {} as Record<
    string,
    { marketGroup: string | null; market: string | null }
  >,
  // Stato salvato per la navigazione back - Live
  // @ts-ignore aggiunta dinamica su SportState
  savedLiveSportState: null as string | null,
  betOutcome: {
    open: false,
    type: 'error',
    outcome: null
  } as BetOutcomeModal,
  // @ts-ignore aggiunta dinamica su SportState
  showCart: false as boolean,

  openMyBetModal: false as boolean,
  // @ts-ignore aggiunta dinamica su SportState
  openSportTree: false as boolean
};

const getters = {
  getSports: (state: SportState) => state.sports,
  getSportsAntepost: (state: SportState) => state.sportsAntepost,
  getFeaturedTournaments: (state: SportState) => state.tournaments,
  getLoading: (state: SportState) => state.loading,
  getLoadingLive: (state: SportState) => state.loadingLive,
  getLoadingTournaments: (state: SportState) => state.loadingTournaments,
  getError: (state: SportState) => state.error,
  getTournamentsError: (state: SportState) => state.tournamentsError,
  getCategoriesError: (state: SportState) => state.categoriesError,
  hasSports: (state: SportState) =>
    state.sports.length > 0 || state.sportsAntepost.length > 0,
  hasTournaments: (state: SportState) => state.tournaments.length > 0,
  getCategories: (state: SportState) => state.categories,
  getCategoriesBySport: (state: SportState) => (sportId: string) =>
    state.categories[sportId] || [],
  getTournamentsByCategory: (state: SportState) => (categoryId: string) =>
    state.tournamentsByCategory[categoryId] || [],
  getFeaturedTournamentsBySport:
    (state: SportState) => (sportId: string | number) =>
      state.featuredTournamnetsBySport[sportId.toString()] || [],
  getAllEventsByTournament: (state: SportState) => state.eventsByTournament,
  getEventsByTournament: (state: SportState) => (tournamentId: string) =>
    state.eventsByTournament[tournamentId] || [],
  getAllPreviewByTournament: (state: SportState) => state.previewByTournament,
  getPreviewByTournament: (state: SportState) => (tournamentId: string) =>
    state.previewByTournament[tournamentId] || [],
  getHeaderSignByTournament: (state: SportState) => (tournamentId: string) =>
    state.headerSignByTournament[tournamentId],
  getEventById: (state: SportState) => (eventId: string | number) =>
    state.eventsById[eventId.toString()] || null,
  getMarketGroupsForTournament: (state: SportState) => (tournamentId: string) =>
    state.marketsByTournament?.[tournamentId] || [],
  getLiveEventsBySport:
    (state: SportState) => (sportId: string | number | null) =>
      sportId != null
        ? state.liveEventsBySport?.[sportId.toString()] || null
        : null,
  getLiveSportsAvailable: (state: SportState) => state.liveSportsAvailable,
  getLiveEventById: (state: SportState) => (eventId: string | number) =>
    state.liveEventById?.[eventId.toString()] || null,
  getFieldStatistics: (state: SportState) => state.fieldStatistics,
  getFieldStatisticsSportId: (state: SportState) =>
    state.fieldStatistics?.sportId ?? null,
  getFieldStatisticsEventId: (state: SportState) =>
    state.fieldStatistics?.eventId ?? null,
  getActiveLiveSport: (state: SportState) => state.activeLiveSport,
  // Getter per accedere velocemente alla regola per source
  getStatsIframeRuleBySource: (state: any) => (source: string | number) =>
    (state.statsIframesRulesMap || {})[String(source)] || null,
  getStatsIframesRulesMap: (state: any) => state.statsIframesRulesMap || {},
  // Getter per verificare se un outcome è stato aggiornato di recente (negli ultimi 1000ms)
  isOutcomeRecentlyUpdated: (state: SportState) => (uniqueId: string) => {
    const timestamp = state.recentlyUpdatedOutcomes?.[uniqueId];
    if (!timestamp) return false;
    return Date.now() - timestamp < 1000; // 1 secondo
  },
  // Getter per verificare se un outcome è presente nel carrello
  isOutcomeInCart:
    (state: SportState, getters: any, rootState: any, rootGetters: any) =>
    (uniqueId: string) => {
      const cartOdds = rootGetters['cart/getBetOdds'] || [];
      return cartOdds.some((odd: any) => odd.unique_id === uniqueId);
    },
  showOddsUpdateMessage: (
    state: SportState & { showOddsUpdateMessage?: boolean }
  ) => {
    // @ts-ignore aggiunta dinamica su SportState
    return state.showOddsUpdateMessage || false;
  },
  // Getters per gestione errori
  getBetError: (state: SportState & { betError?: BetErrorState | null }) => {
    return (state as any).betError || null;
  },
  getBetErrorCode: (
    state: SportState & { betError?: BetErrorState | null },
    getters: any,
    rootState: any,
    rootGetters: any
  ) => {
    const betError = (state as any).betError;
    if (!betError) return '';
    // Restituisce la chiave i18n, il componente userà $t per tradurre
    return betError.errorCode || '';
  },
  getBetErrorReason: (
    state: SportState & { betError?: BetErrorState | null },
    getters: any,
    rootState: any,
    rootGetters: any
  ) => {
    const betError = (state as any).betError;
    if (!betError) return 'n.d.';
    // Restituisce la chiave i18n o il testo diretto (per external_save_failed con reason)
    return betError.errorReason || 'n.d.';
  },
  getCartValidationErrors: (
    state: SportState & { cartValidationErrors?: CartValidationError[] }
  ) => {
    return (state as any).cartValidationErrors || [];
  },
  hasBetError: (state: SportState & { betError?: BetErrorState | null }) => {
    return !!(state as any).betError;
  },
  /**
   * Determina se mostrare la modale di errore del carrello
   *
   * La modale viene mostrata per tutti gli errori TRANNE:
   * - update_odds (quando getOddsVariation è attivo e si riprova automaticamente)
   *
   * La modale VIENE mostrata per:
   * - Utente non autorizzato
   * - update_odds con quote disabilitate/scadute
   * - update_odds senza oddsVariation (dopo aggiornamento manuale quote)
   * - Tutti gli altri errori generici
   */
  shouldShowBetErrorDialog: (
    state: SportState & { betError?: BetErrorState | null }
  ) => {
    const betError = (state as any).betError;
    if (!betError || !betError.response) return false;
    // Mostra sempre per errori di autenticazione
    if (betError.response.message === 'User Not authorized!!!') return true;
    // Mostra per errori con error_code (tutti tranne update_odds con retry automatico)
    if (betError.response.error_code) return true;
    // Mostra per errori con reason ma senza error_code
    if (betError.response.reason) return true;
    return false;
  },
  // Getters per persistenza stato navigazione
  getSavedTournamentMarketState:
    (
      state: SportState & {
        savedTournamentMarketState?: Record<
          string,
          { marketGroup: string | null; market: string | null }
        >;
      }
    ) =>
    (tournamentId: string) => {
      return (state as any).savedTournamentMarketState?.[tournamentId] || null;
    },
  getSavedLiveSportState: (
    state: SportState & { savedLiveSportState?: string | null }
  ) => {
    return (state as any).savedLiveSportState || null;
  },
  getBetOutcome: (state: SportState) => state.betOutcome,
  getOddsToUpdate: (state: SportState) => state.betOutcome?.oddsToUpdate || [],
  getShowCart: (state: SportState) => (state as any).showCart || false,
  getOpenMyBetModal: (state: SportState) =>
    (state as any).openMyBetModal || false,
  getOpenSportTree: (state: SportState) => (state as any).openSportTree || false
};

const actions = {
  // Recupera le regole stats-iframes e salva una mappa { source -> rule }
  async fetchAndStoreStatsIframesRulesMap({
    commit,
    rootGetters,
    getters
  }: any) {
    if (
      getters.getStatsIframesRulesMap &&
      Object.keys(getters.getStatsIframesRulesMap).length > 0
    ) {
      console.log('StatsIframesRulesMap already loaded');
      return;
    }
    const { getStatsIframes } = useApiV3();
    const data: StatsIframesResponse = await getStatsIframes();
    const platformId = rootGetters['settings/getPlatformId'];
    const platform: StatsIframePlatform | undefined =
      (data?.platforms || []).find(
        (p: StatsIframePlatform) => p.platformId === platformId
      ) || (data?.platforms || [])[0];

    const rulesArray: StatsIframeRule[] = Array.isArray(platform?.rules)
      ? platform!.rules
      : [];

    const map = rulesArray.reduce(
      (acc: Record<string, StatsIframeRule>, rule: StatsIframeRule) => {
        if (rule && (rule as any).source != null) {
          acc[String(rule.source)] = rule;
        }
        return acc;
      },
      {} as Record<string, StatsIframeRule>
    );

    commit('setStatsIframesRulesMap', map);
  },
  // FieldStatistics setters (actions -> commit mutations)
  setFieldStatistics(
    { commit }: any,
    payload: Partial<Pick<FieldStatisticsState, 'sportId' | 'eventId'>>
  ) {
    commit('setFieldStatistics', payload);
  },
  setFieldStatisticsSportId(
    { commit }: any,
    payload: { sportId: string | number | null }
  ) {
    commit('setFieldStatisticsSportId', payload.sportId);
  },
  setFieldStatisticsEventId(
    { commit }: any,
    payload: { eventId: string | number | null }
  ) {
    commit('setFieldStatisticsEventId', payload.eventId);
  },
  // Imposta lo sport live attivo e sincronizza anche fieldStatistics.sportId
  setActiveLiveSport(
    { commit, dispatch }: any,
    payload: { sportId: string | number | null }
  ) {
    commit('setActiveLiveSport', payload.sportId);
    // Mantiene allineato anche il fieldStatistics.sportId
    dispatch('setFieldStatisticsSportId', { sportId: payload.sportId });
  },
  async fetchSportsIfNeeded({ commit, getters }: any) {
    console.log('fetchSportsIfNeeded called');

    // Controlla se gli sport sono già presenti
    if (getters.hasSports) {
      console.log('Sports already loaded');
      return;
    }

    commit('setLoading', true);
    commit('setError', null);

    try {
      console.log('Importing useApiV3...');
      const { useApiV3 } = await import('../../composables/apiV3');
      console.log('useApiV3 imported successfully');

      const { getSports } = useApiV3();
      console.log('Calling getSports...');

      const response: SportsApiResponse = await getSports();
      console.log('Response received:', response);

      commit('setSports', response.sports);
      commit('setSportsAntepost', response.sportsAntepost);

      console.log('Sports loaded successfully');
    } catch (error) {
      console.error('Error in fetchSportsIfNeeded:', error);
      commit(
        'setError',
        error instanceof Error
          ? error.message
          : 'Errore nel caricamento degli sport'
      );
    } finally {
      commit('setLoading', false);
    }
  },
  async fetchLiveEventDetailsLive(
    { state, commit }: any,
    payload: {
      sportId: string | number;
      categoryId: string | number;
      tournamentId: string | number;
      eventId: string | number;
      groupId?: string | number | null;
    }
  ) {
    const key = payload.eventId.toString();

    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { GetEventDetailsLive } = useApiV3();
      const response: LiveEventDetailsResponse = await GetEventDetailsLive(
        payload.sportId.toString(),
        payload.categoryId.toString(),
        payload.tournamentId.toString(),
        payload.eventId.toString(),
        payload.groupId != null ? payload.groupId.toString() : null
      );

      // mappa odds live a PreviewOdd[]
      const previewOdds: import('../../../types/sport').PreviewOdd[] = (
        response?.event?.odds || []
      ).map((o: any) => ({
        id: Number(o.id),
        unique_id: String(o.unique_id || o.id),
        outcome: (o.outcome || []).map((out: any) => ({
          unique_id: String(out.unique_id || ''),
          outcome_id: Number(out.outcome_id),
          outcome_odd_value: String(out.value != null ? out.value : ''),
          status: Number(out.status || 0),
          name: String(out.name || ''),
          outcome_alias: String(
            out.outcome_alias || out.alias || out.outcomeAlias || ''
          ),
          outcome_name: String(out.outcome_name || out.name || ''),
          id_outcome: Number(out.id_outcome || out.outcome_id || 0),
          ext_id: String(out.ext_id || out.external_id || ''),
          lastUpdate: String(out.lastUpdate || ''),
          sort: Number(out.sort || 0),
          alias: String(
            out.alias || out.outcome_alias || out.outcomeAlias || ''
          ),
          odd_percent: String(out.odd_percent || out.oddPercent || ''),
          outcome_odd_value_for_order: String(
            out.value != null ? out.value : ''
          ),
          active: true,
          specifier: String(o.specifier || ''),
          type: 'live',
          ...(out.Line != null && { Line: out.Line }),
          ...(out.BaseLine != null && { BaseLine: out.BaseLine })
        })),
        sort: Number(o.sort || 0),
        specifier: String(o.specifier || ''),
        specifierSort: Number(o.specifierSort || 0),
        marketName: String(o.marketName || ''),
        multiplicity: String(o.multiplicity || '1-30'),
        multiplicityMin: Number(o.multiplicityMin || 1),
        multiplicityMax: Number(o.multiplicityMax || 30),
        odd_with_specifier: Boolean(
          o.specifier && String(o.specifier).toLowerCase() !== 'nospec'
        )
      }));

      const initialGroupId = (
        payload.groupId != null
          ? payload.groupId.toString()
          : (response.marketsGroups && response.marketsGroups[0]?._id) ||
            'default'
      ) as string;

      const exists =
        (state.liveEventById && (state.liveEventById as any)[key]) || null;
      if (exists) {
        // Aggiorna l'intero event mantenendo gli altri campi dell'entry
        commit('setLiveEventEntity', {
          eventId: key,
          event: {
            ...(response.event as LiveEventDetailsEvent),
            odds: normalizePreviewOddsToObject(previewOdds) as unknown as any
          } as LiveEventDetailsEvent
        });
        // Aggiorna sempre anche i marketsGroups quando disponibili
        if (Array.isArray(response.marketsGroups)) {
          commit('setLiveEventMarketsGroups', {
            eventId: key,
            marketsGroups: response.marketsGroups
          });
        }
        // Aggiorna anche lo store 'odds' per compatibilità con LiveMatchTracker.vue
        // Struttura minima necessaria: events_live_detail[0].sport.id e events_live_detail[0].event.id
        const dataEventForOdds = {
          source: Number(payload.eventId),
          sport: { id: Number(payload.sportId) },
          event: { id: Number(payload.eventId) }
        } as any;
        commit(
          'odds/SET_EVENTS_LIVE_DETAIL_ACTIVE',
          [Number(payload.eventId)],
          {
            root: true
          }
        );
        commit('odds/SET_EVENT_LIVE_DETAIL', [dataEventForOdds], {
          root: true
        });
      } else {
        // Crea la nuova entry completa se non esiste
        commit('setLiveEventById', {
          eventId: key,
          data: {
            event: {
              ...(response.event as LiveEventDetailsEvent),
              odds: normalizePreviewOddsToObject(previewOdds) as unknown as any
            } as LiveEventDetailsEvent,
            marketsGroups: response.marketsGroups,
            oddsByMarketGroup: {}
          } as LiveEventByID
        });
        // Aggiorna anche lo store 'odds' per compatibilità con LiveMatchTracker.vue
        const dataEventForOdds = {
          source: Number(payload.eventId),
          sport: { id: Number(payload.sportId) },
          event: { id: Number(payload.eventId) }
        } as any;
        commit(
          'odds/SET_EVENTS_LIVE_DETAIL_ACTIVE',
          [Number(payload.eventId)],
          {
            root: true
          }
        );
        commit('odds/SET_EVENT_LIVE_DETAIL', [dataEventForOdds], {
          root: true
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchLiveEventDetailsLive:', error);
    }
  },

  async fetchLiveEventGroupOdds(
    { state, commit }: any,
    payload: {
      sportId: string | number;
      categoryId: string | number;
      tournamentId: string | number;
      eventId: string | number;
      groupId: string | number;
    }
  ) {
    const key = payload.eventId.toString();
    const groupKey = payload.groupId.toString();

    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { GetEventDetailsLive } = useApiV3();
      const response: LiveEventDetailsResponse = await GetEventDetailsLive(
        payload.sportId.toString(),
        payload.categoryId.toString(),
        payload.tournamentId.toString(),
        payload.eventId.toString(),
        groupKey
      );

      const previewOdds: import('../../../types/sport').PreviewOdd[] = (
        response?.event?.odds || []
      ).map((o: any) => ({
        id: Number(o.id),
        unique_id: String(o.unique_id || o.id),
        outcome: (o.outcome || []).map((out: any) => ({
          unique_id: String(out.unique_id || ''),
          outcome_id: Number(out.outcome_id),
          outcome_odd_value: String(out.value != null ? out.value : ''),
          status: Number(out.status || 0),
          name: String(out.name || ''),
          outcome_alias: String(
            out.outcome_alias || out.alias || out.outcomeAlias || ''
          ),
          outcome_name: String(out.outcome_name || out.name || ''),
          id_outcome: Number(out.id_outcome || out.outcome_id || 0),
          ext_id: String(out.ext_id || out.external_id || ''),
          lastUpdate: String(out.lastUpdate || ''),
          sort: Number(out.sort || 0),
          alias: String(
            out.alias || out.outcome_alias || out.outcomeAlias || ''
          ),
          odd_percent: String(out.odd_percent || out.oddPercent || ''),
          outcome_odd_value_for_order: String(
            out.value != null ? out.value : ''
          ),
          active: true,
          specifier: String(o.specifier || ''),
          type: 'live',
          ...(out.Line != null && { Line: out.Line }),
          ...(out.BaseLine != null && { BaseLine: out.BaseLine })
        })),
        sort: Number(o.sort || 0),
        specifier: String(o.specifier || ''),
        specifierSort: Number(o.specifierSort || 0),
        marketName: String(o.marketName || ''),
        multiplicity: String(o.multiplicity || '1-30'),
        multiplicityMin: Number(o.multiplicityMin || 1),
        multiplicityMax: Number(o.multiplicityMax || 30),
        odd_with_specifier: Boolean(
          o.specifier && String(o.specifier).toLowerCase() !== 'nospec'
        ),
        ...(o.mainLine != null && { mainLine: String(o.mainLine) })
      }));

      // Sovrascrive le odds dentro l'evento corrente con oggetto normalizzato
      commit('setLiveEventOddsInEvent', {
        eventId: key,
        odds: normalizePreviewOddsToObject(previewOdds) as unknown as any
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchLiveEventGroupOdds:', error);
    }
  },

  /*  async fetchCategoriesIfNeeded(
    { state, commit }: any,
    payload: { sportId: string }
  ) {
    const { sportId } = payload;

    const categoriesForSport = state.categories[sportId];
    if (categoriesForSport && categoriesForSport.length > 0) {
      console.log('Categorie già caricate');
      return;
    }

    commit('setLoadingCategories', true);
    commit('setCategoriesError', null);

    try {
      const api = useApiV3();
      const response: CategoriesApiResponse = await api.getCategoriesForSport(
        sportId
      );

      if (response && response.categories) {
        // Assicuriamoci che categories sia un array
        commit('setCategoriesForSport', {
          sportId,
          categories: response.categories
        });

        console.log('Categorie caricate:', this.categories.length);
      } else {
        commit('setCategoriesError', 'Formato risposta non valido');
      }
    } catch (err) {
      commit('setCategoriesError', 'Errore nel caricamento');
      console.error(err);
    } finally {
      commit('setLoadingCategories', false);
    }
  },
 */
  async fetchTournamentsIfNeeded({ commit, getters }: any) {
    console.log('fetchTournamentsIfNeeded called');

    // Controlla se i tornei sono già presenti
    if (getters.hasTournaments) {
      console.log('Tournaments already loaded');
      return;
    }

    commit('setLoadingTournaments', true);
    commit('setTournamentsError', null);

    try {
      console.log('Importing useApiV3 for tournaments...');
      const { useApiV3 } = await import('../../composables/apiV3');
      console.log('useApiV3 imported successfully for tournaments');

      const { getFeaturedTournaments } = useApiV3();
      console.log('Calling getTournaments...');

      const response: TournamentsApiResponse = await getFeaturedTournaments();
      console.log('Tournaments response received:', response);

      commit('setTournaments', response.featuredTournaments);

      console.log('Tournaments loaded successfully');
    } catch (error) {
      console.error('Error in fetchTournamentsIfNeeded:', error);
      commit(
        'setTournamentsError',
        error instanceof Error
          ? error.message
          : 'Errore nel caricamento dei tornei'
      );
    } finally {
      commit('setLoadingTournaments', false);
    }
  },

  async fetchLiveSports({ commit }: any) {
    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { getSportsFrontV2 } = useApiV3();
      const response = await getSportsFrontV2();
      commit('setSportLive', response.data || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchLiveSports:', error);
      commit('setSportLive', []);
    }
  },

  async fetchLiveEventsCountBySport(
    { state, commit }: any,
    payload: { sportId?: number; sport_id?: string; lang?: string }
  ) {
    commit('setLoadingLive', true);
    const key = payload.sportId != null ? payload.sportId.toString() : '';
    // sempre fetch dei conteggi live per sport

    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { getLiveEventsCountBySport } = useApiV3();
      const response = await getLiveEventsCountBySport({
        lang: payload.lang,
        sportId: payload.sportId,
        sport_id: payload.sport_id
      });

      // salva sportsAvailable in store
      commit('setLiveSportsAvailable', response.result?.sportsAvailable || []);

      // determina sportId da usare per la chiave se non passato
      let finalSportId: string | null = key || null;
      if (!finalSportId) {
        const first = (response.result?.sportsAvailable || [])[0];
        if (first && first.id != null) {
          finalSportId = first.id.toString();
        }
      }

      if (finalSportId) {
        commit('setLiveEventsBySport', {
          sportId: finalSportId,
          data: response.result
        });

        // Seed: per ogni evento presente nei gruppi, crea/aggiorna una entry in liveEventById (senza toccare marketsGroups)
        try {
          const groups: any[] = Array.isArray(response.result?.groupedEvents)
            ? response.result.groupedEvents
            : [];
          for (const g of groups) {
            const eventsArray: any[] = Array.isArray(g) ? g : [];
            for (const ev of eventsArray) {
              const oddsArray: any[] = Array.isArray(ev?.odds) ? ev.odds : [];
              // Marca come live e normalizza
              const oddsWithType = addTypeToPreviewOddList(
                oddsArray as unknown as import('../../../types/sport').PreviewOdd[],
                'live'
              );
              const normalizedOdds = normalizePreviewOddsToObject(
                oddsWithType as any
              ) as unknown as any;

              const evKey = String(ev.id);
              const existing = state.liveEventById?.[evKey] || null;
              if (existing) {
                // Preserva completamente i dettagli già presenti: non sovrascrivere
                continue;
              } else {
                // Crea una nuova entry se non esiste
                commit('setLiveEventById', {
                  eventId: evKey,
                  data: {
                    event: {
                      ...(ev as any),
                      odds: normalizedOdds
                    } as any,
                    marketsGroups: [],
                    oddsByMarketGroup: {}
                  } as any
                });
              }
            }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            'Error seeding liveEventById from liveEventsBySport:',
            e
          );
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchLiveEventsCountBySport:', error);
    } finally {
      commit('setLoadingLive', false);
    }
  },

  // Variante che fa l'override delle odds dentro liveEventById[eventId].event
  async fetchLiveEventsCountBySportOverrideOdds(
    { state, commit }: any,
    payload: { sportId?: number; sport_id?: string; lang?: string }
  ) {
    commit('setLoadingLive', true);
    const key = payload.sportId != null ? payload.sportId.toString() : '';

    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { getLiveEventsCountBySport } = useApiV3();
      const response = await getLiveEventsCountBySport({
        lang: payload.lang,
        sportId: payload.sportId,
        sport_id: payload.sport_id
      });

      // Aggiorna sempre liveSportsAvailable
      commit('setLiveSportsAvailable', response.result?.sportsAvailable || []);

      // Determina sportId finale
      let finalSportId: string | null = key || null;
      if (!finalSportId) {
        const first = (response.result?.sportsAvailable || [])[0];
        if (first && first.id != null) {
          finalSportId = first.id.toString();
        }
      }

      if (finalSportId) {
        commit('setLiveEventsBySport', {
          sportId: finalSportId,
          data: response.result
        });

        // Seed/aggiornamento: crea o sovrascrive SEMPRE le odds dell'evento
        try {
          const groups: any[] = Array.isArray(response.result?.groupedEvents)
            ? response.result.groupedEvents
            : [];
          for (const g of groups) {
            const eventsArray: any[] = Array.isArray(g) ? g : [];
            for (const ev of eventsArray) {
              const oddsArray: any[] = Array.isArray(ev?.odds) ? ev.odds : [];
              // Marca come live e normalizza
              const oddsWithType = addTypeToPreviewOddList(
                oddsArray as unknown as import('../../../types/sport').PreviewOdd[],
                'live'
              );
              const normalizedOdds = normalizePreviewOddsToObject(
                oddsWithType as any
              ) as unknown as any;

              const evKey = String(ev.id);
              const existing = state.liveEventById?.[evKey] || null;
              if (existing) {
                commit('setLiveEventOddsInEvent', {
                  eventId: evKey,
                  odds: normalizedOdds
                });
              } else {
                commit('setLiveEventById', {
                  eventId: evKey,
                  data: {
                    event: {
                      ...(ev as any),
                      odds: normalizedOdds
                    } as any,
                    marketsGroups: [],
                    oddsByMarketGroup: {}
                  } as any
                });
              }
            }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            'Error seeding (override) liveEventById from liveEventsBySport:',
            e
          );
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchLiveEventsCountBySportOverrideOdds:', error);
    } finally {
      commit('setLoadingLive', false);
    }
  },

  async fetchTournamentsForCategoryIfNeeded(
    { state, commit }: any,
    payload: { sportId: string; categoryId: string }
  ) {
    const { sportId, categoryId } = payload;
    console.log(
      'fetchTournamentsForCategoryIfNeeded called',
      sportId,
      categoryId
    );
    if (
      state.tournamentsByCategory[categoryId] &&
      state.tournamentsByCategory[categoryId].length > 0
    ) {
      console.log('Tournaments for category already loaded');
      return;
    }

    commit('setLoadingTournaments', true);
    commit('setTournamentsError', null);

    try {
      console.log('Importing useApiV3 for tournaments by category...');
      const { useApiV3 } = await import('../../composables/apiV3');
      console.log('useApiV3 imported successfully for tournaments by category');

      const { getTournamentsForSportCategory } = useApiV3();
      console.log('Calling getTournamentsForSportCategory...');

      const response: TournamentsForSportCategoryResponse =
        await getTournamentsForSportCategory(
          sportId.toString(),
          categoryId.toString()
        );
      console.log('Tournaments by category response received:', response);

      commit('setTournamentsForCategory', {
        categoryId,
        tournaments: response.tournaments
      });

      console.log('Tournaments by category loaded successfully');
    } catch (error) {
      console.error('Error in fetchTournamentsForCategoryIfNeeded:', error);
      commit(
        'setTournamentsError',
        error instanceof Error
          ? error.message
          : 'Errore nel caricamento dei tornei per categoria'
      );
    } finally {
      commit('setLoadingTournaments', false);
    }
  },

  async forceFetchSports({ commit }: any) {
    console.log('forceFetchSports called');

    commit('setLoading', true);
    commit('setError', null);

    try {
      console.log('Importing useApiV3...');
      const { useApiV3 } = await import('../../composables/apiV3');
      console.log('useApiV3 imported successfully');

      const { getSports } = useApiV3();
      console.log('Calling getSports (force)...');

      const response: SportsApiResponse = await getSports();
      console.log('Response received (force):', response);

      commit('setSports', response.sports);
      commit('setSportsAntepost', response.sportsAntepost);

      console.log('Sports loaded successfully (force)');
    } catch (error) {
      console.error('Error in forceFetchSports:', error);
      commit(
        'setError',
        error instanceof Error
          ? error.message
          : 'Errore nel caricamento degli sport'
      );
    } finally {
      commit('setLoading', false);
    }
  },

  async forceFetchTournaments({ commit }: any) {
    console.log('forceFetchTournaments called');

    commit('setLoadingTournaments', true);
    commit('setTournamentsError', null);

    try {
      console.log('Importing useApiV3 for tournaments (force)...');
      const { useApiV3 } = await import('../../composables/apiV3');
      console.log('useApiV3 imported successfully for tournaments (force)');

      const { getFeaturedTournaments } = useApiV3();
      console.log('Calling getTournaments (force)...');

      const response: TournamentsApiResponse = await getFeaturedTournaments();
      console.log('Tournaments response received (force):', response);

      commit('setTournaments', response.featuredTournaments);

      console.log('Tournaments loaded successfully (force)');
    } catch (error) {
      console.error('Error in forceFetchTournaments:', error);
      commit(
        'setTournamentsError',
        error instanceof Error
          ? error.message
          : 'Errore nel caricamento dei tornei'
      );
    } finally {
      commit('setLoadingTournaments', false);
    }
  },

  async fetchCategoriesIfNeeded(
    { state, commit }: any,
    payload: { sportId: string }
  ) {
    console.log('fetchCategoriesIfNeeded called', payload);
    const sportId = String(payload.sportId);
    const map = state.categories as CategoriesBySport;
    if (map[sportId]?.length) {
      console.log('Categories already loaded');
      return;
    }

    commit('setLoadingCategories', true);
    commit('setCategoriesError', null);

    try {
      console.log('Importing useApiV3 for categories...');
      const { useApiV3 } = await import('../../composables/apiV3');
      console.log('useApiV3 imported successfully for categories');

      const { getCategoriesForSport } = useApiV3();
      console.log('Calling getCategories...');

      const response: CategoriesApiResponse = await getCategoriesForSport(
        sportId
      );
      console.log('Categories response received:', response);
      commit('setCategoriesForSport', {
        sportId,
        categories: response.categories
      });

      console.log('Categories loaded successfully');
    } catch (error) {
      console.error('Error in fetchCategoriesIfNeeded:', error);
      commit(
        'setCategoriesError',
        error instanceof Error
          ? error.message
          : 'Errore nel caricamento delle categorie'
      );
    } finally {
      commit('setLoadingCategories', false);
    }
  },

  async fetchFeaturedTournamentsBySportIfNeeded(
    { state, commit }: any,
    payload: { sportId: string | number }
  ) {
    console.log('fetchFeaturedTournamentsBySportIfNeeded called', payload);
    const sportId = String(payload.sportId);

    // Controlla se i tornei per questo sportId sono già presenti nello store
    if (state.featuredTournamnetsBySport[sportId]?.length) {
      console.log('Featured tournaments for sport already loaded');
      return;
    }

    commit('setLoadingTournaments', true);
    commit('setTournamentsError', null);

    try {
      console.log('Importing useApiV3 for featured tournaments by sport...');
      const { useApiV3 } = await import('../../composables/apiV3');
      console.log(
        'useApiV3 imported successfully for featured tournaments by sport'
      );

      const { getFeaturedTournaments } = useApiV3();
      console.log('Calling getFeaturedTournaments with sportId...');

      const response: TournamentsApiResponse = await getFeaturedTournaments(
        sportId
      );
      console.log('Featured tournaments by sport response received:', response);

      commit('setFeaturedTournamentsBySport', {
        sportId,
        tournaments: response.featuredTournaments
      });

      console.log('Featured tournaments by sport loaded successfully');
    } catch (error) {
      console.error('Error in fetchFeaturedTournamentsBySportIfNeeded:', error);
      commit(
        'setTournamentsError',
        error instanceof Error
          ? error.message
          : 'Errore nel caricamento dei tornei featured per sport'
      );
    } finally {
      commit('setLoadingTournaments', false);
    }
  },

  async fetchMarketGroupsForTournamentIfNeeded(
    { state, commit }: any,
    payload: {
      sportId: string | number;
      categoryId: string;
      tournamentId: string;
    }
  ) {
    const tournamentKey = payload.tournamentId.toString();

    if (state.marketsByTournament?.[tournamentKey]?.length) {
      return;
    }

    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { getMarketGroupsForTournament } = useApiV3();
      const groups = await getMarketGroupsForTournament(
        payload.sportId,
        payload.categoryId,
        payload.tournamentId
      );

      if (Array.isArray(groups)) {
        commit('setMarketGroupsForTournament', {
          tournamentId: tournamentKey,
          groups
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchMarketGroupsForTournamentIfNeeded:', error);
    }
  },

  clearSports({ commit }: any) {
    console.log('clearSports called');
    commit('setSports', []);
    commit('setSportsAntepost', []);
    commit('setError', null);
  },

  clearTournaments({ commit }: any) {
    console.log('clearTournaments called');
    commit('setTournaments', []);
    commit('setTournamentsError', null);
  },

  async fetchEventDetailsIfNeeded(
    { state, commit }: any,
    payload: {
      sportId: string | number;
      categoryId: string | number;
      tournamentId: string | number;
      eventId: string | number;
    }
  ) {
    const key = payload.eventId.toString();
    if (state.eventsById[key]) return;

    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { GetEventDetails } = useApiV3();
      const response = await GetEventDetails(
        payload.sportId.toString(),
        payload.categoryId.toString(),
        payload.tournamentId.toString(),
        payload.eventId.toString(),
        null
      );

      // Marca le odds con type 'prematch' nelle strutture ricevute
      const eventWithType = {
        ...(response.event as any),
        odds: addTypeToPreviewOddArray(
          response.event?.odds as unknown as PreviewOdd[],
          'prematch'
        )
      } as EventDetailsEvent;

      commit('setEventById', {
        eventId: key,
        event: {
          event: eventWithType,
          marketsGroups: response.marketsGroups,
          oddsByMarketGroup: {}
        }
      });
      commit('setEventOddsByMarketGroup', {
        eventId: key,
        marketGroupId: response.marketsGroups[0]._id,
        odds: addTypeToPreviewOddArray(
          response.event?.odds as unknown as PreviewOdd[],
          'prematch'
        )
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchEventDetailsIfNeeded:', error);
    }
  },

  async fetchEventMarketGroupsIfNeeded(
    { state, commit }: any,
    payload: {
      eventId: string;
      groupId: string;
      sportId: string;
      categoryId: string;
      tournamentId: string;
    }
  ) {
    const key = payload.eventId.toString();
    if (state.eventsById[key]?.oddsByMarketGroup[payload.groupId]) return;

    try {
      const { useApiV3 } = await import('../../composables/apiV3');
      const { GetEventDetails } = useApiV3();
      const response = await GetEventDetails(
        payload.sportId.toString(),
        payload.categoryId.toString(),
        payload.tournamentId.toString(),
        payload.eventId.toString(),
        payload.groupId.toString()
      );
      console.log('response', response);
      console.log('response flat', response.event.odds);
      commit('setEventOddsByMarketGroup', {
        eventId: key,
        marketGroupId: payload.groupId,
        odds: addTypeToPreviewOddArray(
          response.event?.odds as unknown as PreviewOdd[],
          'prematch'
        )
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchEventMarketGroupsIfNeeded:', error);
    }
  },

  async fetchEventsByTournament(
    { state, commit }: any,
    payload: { sportId: string; categoryId: string; tournamentId: string }
  ) {
    const key = payload.tournamentId.toString();
    if (state.eventsByTournament[key]) return;

    const { useApiV3 } = await import('../../composables/apiV3');
    const { getPreviewForTournament } = useApiV3();
    const response = await getPreviewForTournament(
      payload.sportId.toString(),
      payload.categoryId.toString(),
      payload.tournamentId.toString()
    );
    console.log('response', response);
    console.log('response events', response.events);
    // Marca tutte le odds degli eventi del torneo come prematch
    const eventsWithType = (response.events || []).map((g: any) => ({
      scheduled: g.scheduled,
      events: (g.events || []).map((ev: any) => ({
        ...ev,
        odds: addTypeToPreviewOddList(ev.odds as any, 'prematch') as any
      }))
    }));

    commit('setEventsByTournament', {
      tournamentId: payload.tournamentId,
      events: eventsWithType as unknown as EventDetailsEvent[]
    });

    // Salva headerSign se presente nella risposta
    if (response.headerSign) {
      commit('setHeaderSignByTournament', {
        tournamentId: payload.tournamentId,
        headerSign: response.headerSign
      });
    }
  },

  async fetchPreviewByTournament(
    { state, commit }: any,
    payload: { sportId: string; categoryId: string; tournamentId: string }
  ) {
    const key = payload.tournamentId.toString();
    if (state.previewByTournament[key]) return;

    const { useApiV3 } = await import('../../composables/apiV3');
    const { getPreviewForTournament } = useApiV3();
    const response = await getPreviewForTournament(
      payload.sportId.toString(),
      payload.categoryId.toString(),
      payload.tournamentId.toString()
    );

    // Marca tutte le odds degli eventi del torneo come prematch
    const eventsWithType = (response.events || []).map((g: any) => ({
      scheduled: g.scheduled,
      events: (g.events || []).map((ev: any) => ({
        ...ev,
        odds: addTypeToPreviewOddList(ev.odds as any, 'prematch') as any
      }))
    }));

    commit('setPreviewByTournament', {
      tournamentId: payload.tournamentId,
      events: eventsWithType as unknown as EventDetailsEvent[]
    });

    // Salva headerSign se presente nella risposta
    if (response.headerSign) {
      commit('setHeaderSignByTournament', {
        tournamentId: payload.tournamentId,
        headerSign: response.headerSign
      });
    }
  },

  async fetchTournamentOddMarket(
    { state, commit }: any,
    payload: {
      sportId: string;
      categoryId: string;
      tournamentId: string;
      marketId: string;
      groupId: string;
    }
  ) {
    try {
      // Controllo iniziale: se abbiamo già dati per questo market, non richiamiamo l'API
      // Cerca prima in previewByTournament, poi in eventsByTournament come fallback
      const existingGroups = (state.previewByTournament[
        payload.tournamentId.toString()
      ] ||
        state.eventsByTournament[payload.tournamentId.toString()] ||
        []) as unknown as PreviewEventGroup[];

      let hasMarketData = false;
      for (const group of existingGroups) {
        for (const event of group.events || []) {
          const found = (event.odds || []).some(
            (odd: PreviewOdd) =>
              odd &&
              odd.id !== undefined &&
              odd.id.toString() === payload.marketId.toString()
          );
          if (found) {
            hasMarketData = true;
            break;
          }
        }
        if (hasMarketData) break;
      }

      if (hasMarketData) {
        return;
      }

      const { useApiV3 } = await import('../../composables/apiV3');
      const { getPreviewForTournamentMarket } = useApiV3();
      const response = await getPreviewForTournamentMarket(
        payload.sportId,
        payload.categoryId,
        payload.tournamentId,
        payload.marketId,
        payload.groupId
      );

      if (response && response.events) {
        commit('mergeTournamentMarketOdds', {
          tournamentId: payload.tournamentId,
          responseEvents: response.events
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchTournamentOddMarket:', error);
    }
  },
  async addOddToCartV3FromSport(
    { dispatch }: any,
    payload: {
      sportId: string;
      sportName: string;
      categoryId: string;
      categoryName: string;
      tournamentId: string;
      tournamentName: string;
      eventId: string | number;
      marketId: string | number;
      outcomeUniqueId?: string;
      outcomeId?: number;
    }
  ): Promise<void> {
    console.log('addOddToCartV3FromSport', payload);
    const built: AddOddToCartV3Payload = await dispatch(
      'buildAddOddToCartV3Payload',
      payload
    );

    await dispatch('cart/addOddToCartV3', built, { root: true });
  },
  async buildAddOddToCartV3Payload(
    { state }: any,
    payload: {
      sportId: string;
      sportName: string;
      categoryId: string;
      categoryName: string;
      tournamentId: string;
      tournamentName: string;
      eventId: string | number;
      marketId: string | number;
      outcomeUniqueId?: string;
      outcomeId?: number;
    }
  ): Promise<AddOddToCartV3Payload> {
    console.log(
      '[buildAddOddToCartV3Payload] INPUT - payload completo:',
      payload
    );
    console.log(
      '[buildAddOddToCartV3Payload] INPUT - state.previewByTournament keys:',
      Object.keys(state.previewByTournament || {})
    );
    console.log(
      '[buildAddOddToCartV3Payload] INPUT - state.eventsByTournament keys:',
      Object.keys(state.eventsByTournament || {})
    );
    console.log(
      '[buildAddOddToCartV3Payload] INPUT - state.eventsById keys:',
      Object.keys(state.eventsById || {})
    );

    const tournamentKey = payload.tournamentId.toString();
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 1 - Recupero gruppi dal tournament, tournamentKey:',
      tournamentKey
    );

    // Cerca prima in eventsByTournament (dove vengono aggiunti i market specifici),
    // poi in previewByTournament come fallback (solo per il market "preview")
    const groups = (state.eventsByTournament[tournamentKey] ||
      state.previewByTournament[tournamentKey] ||
      []) as unknown as PreviewEventGroup[];
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 1 - OUTPUT - groups trovati:',
      groups.length,
      groups
    );

    // 1) Preferisci i dettagli dell'evento, poi eventualmente fallback al preview torneo
    const eventIdKey = payload.eventId.toString();
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 2 - Ricerca eventById, eventIdKey:',
      eventIdKey
    );

    const eventById: EventByID | null = state.eventsById?.[eventIdKey] || null;
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 2 - OUTPUT - eventById trovato:',
      !!eventById,
      eventById
    );

    let eventFromTournament: PreviewEvent | null = null;
    if (!eventById && Array.isArray(groups) && groups.length > 0) {
      console.log(
        '[buildAddOddToCartV3Payload] OPERAZIONE 3 - Ricerca eventFromTournament nei groups'
      );
      for (const group of groups) {
        const found = (group.events || []).find(
          (e: PreviewEvent) => e.id.toString() === payload.eventId.toString()
        );
        if (found) {
          eventFromTournament = found;
          console.log(
            '[buildAddOddToCartV3Payload] OPERAZIONE 3 - OUTPUT - eventFromTournament trovato nel group:',
            group,
            found
          );
          break;
        }
      }
    }
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 3 - OUTPUT finale - eventFromTournament:',
      eventFromTournament
    );

    if (!eventById && !eventFromTournament) {
      console.error('[buildAddOddToCartV3Payload] ERRORE - Evento non trovato');
      throw new Error(
        'Evento non trovato né nel preview torneo né nei dettagli evento'
      );
    }

    // Costruiamo la lista dei mercati (odds) a seconda della sorgente
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 4 - Costruzione oddsList'
    );
    let oddsList: PreviewOdd[] = [];
    if (eventById) {
      console.log(
        '[buildAddOddToCartV3Payload] OPERAZIONE 4 - Usando eventById per oddsList'
      );
      // EventByID: oddsByMarketGroup è Record<string, PreviewOdd[]>
      const oddsFromGroups = eventById.oddsByMarketGroup
        ? (Object.values(eventById.oddsByMarketGroup) as PreviewOdd[][]).flat()
        : [];
      console.log(
        '[buildAddOddToCartV3Payload] OPERAZIONE 4 - oddsFromGroups:',
        oddsFromGroups.length,
        oddsFromGroups
      );
      if (oddsFromGroups.length) {
        oddsList = oddsFromGroups;
        console.log(
          '[buildAddOddToCartV3Payload] OPERAZIONE 4 - Usato oddsFromGroups'
        );
      } else {
        // event.odds è ora PreviewOdd[] invece di EventOddsMap
        oddsList = (eventById.event as EventDetailsEvent).odds || [];
        console.log(
          '[buildAddOddToCartV3Payload] OPERAZIONE 4 - Usato eventById.event?.odds (array):',
          oddsList.length
        );
      }
    } else if (eventFromTournament) {
      console.log(
        '[buildAddOddToCartV3Payload] OPERAZIONE 4 - Usando eventFromTournament per oddsList'
      );
      oddsList = (eventFromTournament.odds || []) as unknown as PreviewOdd[];
    }
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 4 - OUTPUT - oddsList finale:',
      oddsList.length,
      oddsList
    );

    // 2) Trova il mercato all'interno dell'elenco odds
    const marketIdRaw = payload.marketId.toString();
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 5 - Parsing marketId, marketIdRaw:',
      marketIdRaw
    );

    const [marketIdBase, marketSpecifier] = marketIdRaw.includes('#')
      ? marketIdRaw.split('#')
      : [marketIdRaw, null];

    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 5 - OUTPUT - marketIdBase:',
      marketIdBase,
      'marketSpecifier:',
      marketSpecifier
    );

    // Estrai lo spread dall'outcomeUniqueId e costruisci il unique_id del market
    let marketUniqueIdToSearch = marketIdRaw;
    if (payload.outcomeUniqueId && payload.outcomeUniqueId.includes('#')) {
      const parts = payload.outcomeUniqueId.split('#');
      if (parts.length > 1) {
        const specifierPart = parts[1].split(';')[0];
        if (specifierPart) {
          // Costruisci il unique_id del market come marketId#spread
          marketUniqueIdToSearch = `${marketIdBase}#${specifierPart}`;
        }
      }
    }
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 5.1 - marketUniqueIdToSearch:',
      marketUniqueIdToSearch
    );

    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 6 - Ricerca oddMarket in oddsList'
    );
    let oddMarket = (oddsList || []).find((m: PreviewOdd) => {
      if (m.unique_id !== undefined) {
        const matchesByUniqueId =
          m &&
          m.unique_id !== undefined &&
          m.unique_id.toString() === marketUniqueIdToSearch;

        const matchResult = Boolean(matchesByUniqueId);
        if (matchResult) {
          console.log(
            '[buildAddOddToCartV3Payload] OPERAZIONE 6 - Match trovato:',
            {
              m,
              matchesByUniqueId
            }
          );
        }
        return matchResult;
      } else {
        const matchesById =
          m && m.id !== undefined && m.id.toString() === marketIdBase;
        const matchResult = Boolean(matchesById);
        if (matchResult) {
          console.log(
            '[buildAddOddToCartV3Payload] OPERAZIONE 6 - Match trovato:',
            {
              m,
              matchesById
            }
          );
        }
        return matchResult;
      }
    });
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 6 - OUTPUT - oddMarket trovato:',
      !!oddMarket,
      oddMarket
    );
    if (oddMarket) {
      console.log(
        '[buildAddOddToCartV3Payload] OPERAZIONE 6 - OUTPUT - oddMarket trovato:',
        oddMarket
      );
    }

    // Fallback: se non trovato, prova a cercare per chiave nelle mappe (oddsByMarketGroup -> array)
    if (!oddMarket && eventById) {
      console.log(
        '[buildAddOddToCartV3Payload] OPERAZIONE 7 - Fallback ricerca oddMarket negli array'
      );

      // EventByID: cerca direttamente negli array
      const tryFromArray = (
        oddsArray: PreviewOdd[] | undefined | null
      ): PreviewOdd | null => {
        console.log(
          '[buildAddOddToCartV3Payload] OPERAZIONE 7 - tryFromArray chiamato con array:',
          oddsArray?.length
        );
        if (!oddsArray || !Array.isArray(oddsArray)) {
          return null;
        }

        // Cerca prima per unique_id (con o senza spread)
        const foundByUniqueId = oddsArray.find(
          (m: PreviewOdd) =>
            m.unique_id !== undefined &&
            m.unique_id.toString() === marketUniqueIdToSearch
        );
        if (foundByUniqueId) {
          console.log(
            '[buildAddOddToCartV3Payload] OPERAZIONE 7 - Match trovato per unique_id:',
            foundByUniqueId
          );
          return foundByUniqueId;
        }

        // Fallback: cerca per id base
        if (marketSpecifier) {
          const found =
            oddsArray.find(
              (m: PreviewOdd) =>
                m.unique_id === marketIdRaw ||
                m.specifier === marketSpecifier ||
                m.id.toString() === marketIdBase
            ) || null;
          return found;
        }
        return (
          oddsArray.find(
            (m: PreviewOdd) =>
              m.unique_id === marketIdRaw || m.id.toString() === marketIdBase
          ) || null
        );
      };

      // 1) Cerca nei gruppi (ora sono array diretti)
      if (eventById.oddsByMarketGroup) {
        console.log(
          '[buildAddOddToCartV3Payload] OPERAZIONE 7.1 - Ricerca nei oddsByMarketGroup (array):',
          eventById.oddsByMarketGroup
        );
        const groupArrays = Object.values(
          eventById.oddsByMarketGroup
        ) as PreviewOdd[][];
        for (const groupArray of groupArrays) {
          const found = tryFromArray(groupArray);
          if (found) {
            oddMarket = found;
            console.log(
              '[buildAddOddToCartV3Payload] OPERAZIONE 7.1 - OUTPUT - oddMarket trovato nei gruppi:',
              oddMarket
            );
            break;
          }
        }
      }

      // 2) Se ancora non trovato, cerca nell'array dell'evento
      if (!oddMarket) {
        console.log(
          '[buildAddOddToCartV3Payload] OPERAZIONE 7.2 - Ricerca in eventById.event?.odds (array):',
          (eventById.event as EventDetailsEvent).odds?.length
        );
        oddMarket = tryFromArray((eventById.event as EventDetailsEvent).odds);
        if (oddMarket) {
          console.log(
            '[buildAddOddToCartV3Payload] OPERAZIONE 7.2 - OUTPUT - oddMarket trovato in event.odds:',
            oddMarket
          );
        }
      }
    }

    if (!oddMarket) {
      console.error(
        '[buildAddOddToCartV3Payload] ERRORE - Mercato non trovato'
      );
      throw new Error("Mercato richiesto non trovato per l'evento");
    }

    // 3) Trova l'odd all'interno del mercato (per unique_id o outcome_id)
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 8 - Ricerca odd nel mercato'
    );
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 8 - INPUT - payload.outcomeUniqueId:',
      payload.outcomeUniqueId
    );
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 8 - INPUT - payload.outcomeId:',
      payload.outcomeId
    );
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 8 - INPUT - oddMarket.outcome disponibili:',
      oddMarket.outcome?.length,
      oddMarket.outcome
    );

    // Cerca prima per unique_id, poi per outcome_id/id_outcome come fallback
    let odd = (oddMarket.outcome || []).find(o => {
      const byUnique =
        payload.outcomeUniqueId && o.unique_id === payload.outcomeUniqueId;
      if (byUnique) {
        console.log(
          '[buildAddOddToCartV3Payload] OPERAZIONE 8 - Match odd trovato per unique_id:',
          {
            o,
            byUnique: true
          }
        );
        return true;
      }
      return false;
    });

    // Se non trovato per unique_id, cerca per outcome_id/id_outcome
    if (!odd && payload.outcomeId) {
      odd = (oddMarket.outcome || []).find(o => {
        const byId =
          o.outcome_id === payload.outcomeId ||
          o.id_outcome === payload.outcomeId;
        if (byId) {
          console.log(
            '[buildAddOddToCartV3Payload] OPERAZIONE 8 - Match odd trovato per outcome_id:',
            {
              o,
              byId: true,
              outcome_id: o.outcome_id,
              id_outcome: o.id_outcome,
              payload_outcomeId: payload.outcomeId
            }
          );
        }
        return byId;
      });
    }

    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 8 - OUTPUT - odd trovato:',
      !!odd,
      odd
    );
    if (!odd) {
      console.error('[buildAddOddToCartV3Payload] ERRORE - Odd non trovata');
      console.error('[buildAddOddToCartV3Payload] ERRORE - Dettagli ricerca:', {
        outcomeUniqueId: payload.outcomeUniqueId,
        outcomeId: payload.outcomeId,
        availableOutcomes: (oddMarket.outcome || []).map(o => ({
          unique_id: o.unique_id,
          outcome_id: o.outcome_id,
          id_outcome: o.id_outcome
        }))
      });
      throw new Error('Odd richiesta non trovata nel mercato selezionato');
    }

    // 4) Costruisci i blocchi del payload tipizzati
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9 - Costruzione payload finali'
    );

    const sport: BetEntityLoose = {
      id: Number(payload.sportId),
      name: payload.sportName
    } as any;
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9.1 - OUTPUT - sport:',
      sport
    );

    const category: BetEntityLoose = {
      id: Number(payload.categoryId),
      name: payload.categoryName
    } as any;
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9.2 - OUTPUT - category:',
      category
    );

    const tournament: BetEntityLoose = {
      id: Number(payload.tournamentId),
      name: payload.tournamentName
    } as any;
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9.3 - OUTPUT - tournament:',
      tournament
    );

    const baseEvent: any = eventById ? eventById.event : eventFromTournament;
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9.4 - INPUT - baseEvent:',
      baseEvent
    );

    const eventPayload: AddOddEventV3 = {
      id: Number(baseEvent.id),
      scheduled: baseEvent.scheduled,
      home: baseEvent.home,
      away: baseEvent.away,
      odds: addTypeToPreviewOddList(
        oddsList as any,
        'prematch'
      ) as unknown as any,
      type: baseEvent.type,
      quickbet: baseEvent.quickbet
    };
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9.4 - OUTPUT - eventPayload:',
      eventPayload
    );

    const oddMarketPayload: OddMarketIncoming = {
      id: Number(oddMarket.id),
      unique_id: oddMarket.unique_id,
      outcome: oddMarket.outcome as unknown as any[],
      specifier: oddMarket.specifier,
      marketName: oddMarket.marketName,
      multiplicity: oddMarket.multiplicity,
      multiplicityMin: Number(oddMarket.multiplicityMin),
      multiplicityMax: Number(oddMarket.multiplicityMax)
    } as OddMarketIncoming;
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9.5 - OUTPUT - oddMarketPayload:',
      oddMarketPayload
    );

    odd.type = 'prematch';
    const oddPayload: AddOddIncoming = odd as unknown as AddOddIncoming;
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 9.6 - OUTPUT - oddPayload:',
      oddPayload
    );

    const result: AddOddToCartV3Payload = {
      odd: oddPayload,
      oddMarket: oddMarketPayload,
      event: eventPayload,
      sport,
      category,
      tournament
    };
    console.log(
      '[buildAddOddToCartV3Payload] OPERAZIONE 10 - OUTPUT FINALE - result completo:',
      result
    );

    return result;
  },
  async addOddToCartV3FromLive(
    { dispatch }: any,
    payload: {
      sportId: string;
      sportName: string;
      categoryId: string;
      categoryName: string;
      tournamentId: string;
      tournamentName: string;
      eventId: string | number;
      marketId: string | number;
      outcomeUniqueId?: string;
      outcomeId?: number;
    }
  ): Promise<void> {
    console.log('[addOddToCartV3FromLive] INPUT - payload completo:', payload);
    console.log(
      '[addOddToCartV3FromLive] FASE 1 - Chiamata buildAddOddToCartV3PayloadLive'
    );
    const built: AddOddToCartV3Payload = await (dispatch as any)(
      'buildAddOddToCartV3PayloadLive',
      payload
    );
    console.log(
      '[addOddToCartV3FromLive] FASE 1 - OUTPUT - built payload:',
      built
    );

    console.log(
      '[addOddToCartV3FromLive] FASE 2 - Chiamata cart/addOddToCartV3'
    );
    await dispatch('cart/addOddToCartV3', built, { root: true });
    console.log(
      '[addOddToCartV3FromLive] FASE 2 - OUTPUT - Operazione completata'
    );
  },
  async buildAddOddToCartV3PayloadLive(
    { state }: any,
    payload: {
      sportId: string;
      sportName: string;
      categoryId: string;
      categoryName: string;
      tournamentId: string;
      tournamentName: string;
      eventId: string | number;
      marketId: string | number;
      outcomeUniqueId?: string;
      outcomeId?: number;
    }
  ): Promise<AddOddToCartV3Payload> {
    console.log(
      '[buildAddOddToCartV3PayloadLive] INPUT - payload completo:',
      payload
    );
    console.log(
      '[buildAddOddToCartV3PayloadLive] INPUT - state.liveEventById keys:',
      Object.keys(state.liveEventById || {})
    );

    const eventIdKey = payload.eventId.toString();
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 1 - Ricerca evento live, eventIdKey:',
      eventIdKey
    );
    const liveById: LiveEventByID | null =
      (state.liveEventById && state.liveEventById[eventIdKey]) || null;
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 1 - OUTPUT - liveById trovato:',
      !!liveById,
      liveById
    );

    if (!liveById || !liveById.event) {
      console.error(
        '[buildAddOddToCartV3PayloadLive] ERRORE - Evento live non trovato nello store'
      );
      throw new Error('Evento live non trovato nello store');
    }

    // Converte le odds normalizzate (oggetto) in lista PreviewOdd[] con outcome array
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 2 - Conversione odds normalizzate'
    );
    const oddsObj: any = (liveById.event as any).odds || {};
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 2 - INPUT - oddsObj:',
      oddsObj
    );
    const oddsArrayRaw: any[] = Array.isArray(oddsObj)
      ? oddsObj
      : Object.values(oddsObj || {});
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 2 - OUTPUT - oddsArrayRaw:',
      oddsArrayRaw.length,
      oddsArrayRaw
    );

    const normalizeOutcomeArray = (o: any) => {
      const result = (
        Array.isArray(o?.outcome) ? o.outcome : Object.values(o?.outcome || {})
      ) as any[];
      console.log(
        '[buildAddOddToCartV3PayloadLive] FASE 3 - normalizeOutcomeArray - INPUT:',
        o,
        'OUTPUT:',
        result.length,
        result
      );
      return result;
    };

    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 3 - Normalizzazione oddsList'
    );
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 3 - INPUT - oddsArrayRaw da processare:',
      oddsArrayRaw.length,
      'odds'
    );
    const oddsList: PreviewOdd[] = oddsArrayRaw.map((o: any, index: number) => {
      console.log(
        `[buildAddOddToCartV3PayloadLive] FASE 3 - Processing odd ${
          index + 1
        }/${oddsArrayRaw.length}:`,
        {
          id: o.id,
          unique_id: o.unique_id,
          marketName: o.marketName,
          specifier: o.specifier,
          outcomesCount: Array.isArray(o?.outcome)
            ? o.outcome.length
            : Object.keys(o?.outcome || {}).length
        }
      );
      const outcomesArray = normalizeOutcomeArray(o);
      const normalizedOdd = {
        id: Number(o.id),
        unique_id: String(o.unique_id || o.id),
        outcome: outcomesArray.map((out: any, outIndex: number) => {
          const normalizedOutcome = {
            unique_id: String(out.unique_id || ''),
            outcome_id: Number(out.outcome_id),
            outcome_odd_value: String(
              out.outcome_odd_value != null
                ? out.outcome_odd_value
                : out.value != null
                ? out.value
                : ''
            ),
            status: Number(out.status || 0),
            name: String(out.name || ''),
            outcome_alias: String(
              out.outcome_alias || out.alias || out.outcomeAlias || ''
            ),
            outcome_name: String(out.outcome_name || out.name || ''),
            id_outcome: Number(out.id_outcome || out.outcome_id || 0),
            ext_id: String(out.ext_id || out.external_id || ''),
            lastUpdate: String(out.lastUpdate || ''),
            sort: Number(out.sort || out.specifierSort || 0),
            alias: String(
              out.alias || out.outcome_alias || out.outcomeAlias || ''
            ),
            odd_percent: String(
              (out as any).odd_percent || (out as any).oddPercent || ''
            ),
            outcome_odd_value_for_order: String(
              (out as any).outcome_odd_value_for_order != null
                ? (out as any).outcome_odd_value_for_order
                : out.value != null
                ? out.value
                : ''
            ),
            active: true,
            specifier: String(o.specifier || ''),
            type: 'live' as 'live',
            ...(out.Line != null && { Line: out.Line }),
            ...(out.BaseLine != null && { BaseLine: out.BaseLine })
          };
          console.log(
            `[buildAddOddToCartV3PayloadLive] FASE 3 - Normalizzazione outcome ${
              outIndex + 1
            } per odd ${index + 1}:`,
            {
              input: out,
              output: normalizedOutcome
            }
          );
          return normalizedOutcome;
        }),
        sort: Number(o.sort || 0),
        specifier: String(o.specifier || ''),
        specifierSort: Number(o.specifierSort || 0),
        marketName: String(o.marketName || ''),
        multiplicity: String((o as any).multiplicity || '1-30'),
        multiplicityMin: Number((o as any).multiplicityMin || 1),
        multiplicityMax: Number((o as any).multiplicityMax || 30),
        odd_with_specifier: Boolean(
          o.specifier && String(o.specifier).toLowerCase() !== 'nospec'
        )
      };
      console.log(
        `[buildAddOddToCartV3PayloadLive] FASE 3 - Odd ${
          index + 1
        } normalizzata:`,
        {
          id: normalizedOdd.id,
          unique_id: normalizedOdd.unique_id,
          marketName: normalizedOdd.marketName,
          outcomesCount: outcomesArray.length
        }
      );
      return normalizedOdd;
    }) as PreviewOdd[];
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 3 - OUTPUT - oddsList normalizzata:',
      oddsList.length,
      oddsList
    );

    // Trova il mercato
    console.log('[buildAddOddToCartV3PayloadLive] FASE 4 - Ricerca mercato');
    const marketIdRaw = payload.marketId.toString();
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 4 - INPUT - marketIdRaw:',
      marketIdRaw
    );
    const [marketIdBase, marketSpecifier] = marketIdRaw.includes('#')
      ? marketIdRaw.split('#')
      : [marketIdRaw, null];

    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 4 - OUTPUT - marketIdBase:',
      marketIdBase
    );
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 4 - OUTPUT - marketSpecifier:',
      marketSpecifier
    );
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 4 - INPUT - oddsList per ricerca:',
      oddsList.length,
      oddsList
    );
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 4.1 - Ricerca mercato per unique_id'
    );
    let oddMarket = (oddsList || []).find((m: PreviewOdd) => {
      const matchesById =
        m && m.id !== undefined && m.id.toString() === marketIdBase;
      const matchesByUniqueId =
        m &&
        m.unique_id !== undefined &&
        m.unique_id.toString() === marketIdRaw;
      const matchResult = Boolean(matchesByUniqueId);
      if (matchResult) {
        console.log(
          '[buildAddOddToCartV3PayloadLive] FASE 4.1 - Match trovato per unique_id:',
          {
            m,
            matchesByUniqueId
          }
        );
      }
      return matchResult;
    });
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 4.1 - OUTPUT - oddMarket trovato:',
      !!oddMarket,
      oddMarket
    );

    if (!oddMarket) {
      // fallback per specifier
      console.log(
        '[buildAddOddToCartV3PayloadLive] FASE 4.2 - Fallback ricerca mercato per id e specifier'
      );
      oddMarket = (oddsList || []).find((m: PreviewOdd) => {
        const matchesById =
          m && m.id !== undefined && m.id.toString() === marketIdBase;
        if (!matchesById) return false;
        if (!marketSpecifier) {
          console.log(
            '[buildAddOddToCartV3PayloadLive] FASE 4.2 - Match trovato per id senza specifier:',
            m
          );
          return true;
        }
        const matchesSpecifier =
          (m.unique_id && m.unique_id.toString() === marketIdRaw) ||
          (m.specifier && m.specifier.toString() === marketSpecifier);
        if (matchesSpecifier) {
          console.log(
            '[buildAddOddToCartV3PayloadLive] FASE 4.2 - Match trovato per id e specifier:',
            {
              m,
              matchesById,
              matchesSpecifier
            }
          );
        }
        return matchesSpecifier;
      }) as PreviewOdd | undefined;
      console.log(
        '[buildAddOddToCartV3PayloadLive] FASE 4.2 - OUTPUT - oddMarket dal fallback:',
        !!oddMarket,
        oddMarket
      );
    }

    if (!oddMarket) {
      console.error(
        '[buildAddOddToCartV3PayloadLive] ERRORE - Mercato live non trovato'
      );
      throw new Error("Mercato live richiesto non trovato per l'evento");
    }

    console.log('[buildAddOddToCartV3PayloadLive] FASE 5 - Ricerca outcome');
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 5 - INPUT - payload.outcomeUniqueId:',
      payload.outcomeUniqueId
    );
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 5 - INPUT - payload.outcomeId:',
      payload.outcomeId
    );
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 5 - INPUT - oddMarket.outcome disponibili:',
      oddMarket.outcome?.length,
      oddMarket.outcome
    );
    // Trova l'outcome: cerca prima per unique_id, poi per outcome_id/id_outcome come fallback
    let outcome = (oddMarket.outcome || []).find(o => {
      const byUnique =
        payload.outcomeUniqueId && o.unique_id === payload.outcomeUniqueId;
      if (byUnique) {
        console.log(
          '[buildAddOddToCartV3PayloadLive] FASE 5 - Match outcome trovato per unique_id:',
          {
            o,
            byUnique: true
          }
        );
        return true;
      }
      return false;
    });

    // Se non trovato per unique_id, cerca per outcome_id/id_outcome
    if (!outcome && payload.outcomeId) {
      outcome = (oddMarket.outcome || []).find(o => {
        const byId =
          o.outcome_id === payload.outcomeId ||
          o.id_outcome === payload.outcomeId;
        if (byId) {
          console.log(
            '[buildAddOddToCartV3PayloadLive] FASE 5 - Match outcome trovato per outcome_id:',
            {
              o,
              byId: true,
              outcome_id: o.outcome_id,
              id_outcome: o.id_outcome,
              payload_outcomeId: payload.outcomeId
            }
          );
        }
        return byId;
      });
    }

    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 5 - OUTPUT - outcome trovato:',
      !!outcome,
      outcome
    );
    if (!outcome) {
      console.error(
        '[buildAddOddToCartV3PayloadLive] ERRORE - Outcome non trovato'
      );
      console.error(
        '[buildAddOddToCartV3PayloadLive] ERRORE - Dettagli ricerca:',
        {
          outcomeUniqueId: payload.outcomeUniqueId,
          outcomeId: payload.outcomeId,
          availableOutcomes: (oddMarket.outcome || []).map(o => ({
            unique_id: o.unique_id,
            outcome_id: o.outcome_id,
            id_outcome: o.id_outcome
          }))
        }
      );
      throw new Error('Odd live richiesta non trovata nel mercato selezionato');
    }

    // Entità sport/category/tournament con fallback dai dettagli evento live
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 6 - Costruzione entità sport/category/tournament'
    );
    const baseEvent: any = liveById.event as LiveEventDetailsEvent;
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 6 - INPUT - baseEvent:',
      baseEvent
    );
    const fallbackSportName = String(baseEvent?.sportName || '');
    const fallbackCategoryName = String(baseEvent?.categoryName || '');
    const fallbackTournamentName = String(baseEvent?.tournamentName || '');
    console.log('[buildAddOddToCartV3PayloadLive] FASE 6 - Fallback names:', {
      fallbackSportName,
      fallbackCategoryName,
      fallbackTournamentName
    });

    const sport: BetEntityLoose = {
      id: Number(payload.sportId),
      name: payload.sportName || fallbackSportName
    } as any;
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 6.1 - OUTPUT - sport:',
      sport
    );
    const category: BetEntityLoose = {
      id: Number(payload.categoryId),
      name: payload.categoryName || fallbackCategoryName
    } as any;
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 6.2 - OUTPUT - category:',
      category
    );
    const tournament: BetEntityLoose = {
      id: Number(payload.tournamentId),
      name: payload.tournamentName || fallbackTournamentName
    } as any;
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 6.3 - OUTPUT - tournament:',
      tournament
    );

    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 7 - Costruzione eventPayload'
    );
    const eventPayload: AddOddEventV3 = {
      id: Number(baseEvent.id),
      scheduled: baseEvent.scheduled,
      home: baseEvent.home,
      away: baseEvent.away,
      odds: addTypeToPreviewOddList(oddsList as any, 'live') as unknown as any,
      type: baseEvent.type,
      quickbet: (baseEvent as any).quickbet
    };
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 7 - OUTPUT - eventPayload:',
      eventPayload
    );

    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 8 - Costruzione oddMarketPayload'
    );
    const oddMarketPayload: OddMarketIncoming = {
      id: Number(oddMarket.id),
      unique_id: oddMarket.unique_id,
      outcome: (oddMarket.outcome as unknown as any[]) || [],
      specifier: oddMarket.specifier,
      marketName: oddMarket.marketName,
      multiplicity: oddMarket.multiplicity,
      multiplicityMin: Number(oddMarket.multiplicityMin),
      multiplicityMax: Number(oddMarket.multiplicityMax)
    } as OddMarketIncoming;
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 8 - OUTPUT - oddMarketPayload:',
      oddMarketPayload
    );

    // Garantisce alias valorizzato per il carrello e imposta il tipo
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 9 - Preparazione oddPayload'
    );
    const aliasVal =
      (outcome as any).outcome_alias ||
      (outcome as any).alias ||
      (outcome as any).outcome_name ||
      (outcome as any).name ||
      '';
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 9 - INPUT - aliasVal calcolato:',
      aliasVal
    );
    (outcome as any).outcome_alias = String(aliasVal);
    (outcome as any).alias = String(aliasVal);
    (outcome as any).type = 'live';
    const oddPayload: AddOddIncoming = outcome as unknown as AddOddIncoming;
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 9 - OUTPUT - oddPayload:',
      oddPayload
    );

    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 10 - Costruzione risultato finale'
    );
    const result: AddOddToCartV3Payload = {
      odd: oddPayload,
      oddMarket: oddMarketPayload,
      event: eventPayload,
      sport,
      category,
      tournament
    };
    console.log(
      '[buildAddOddToCartV3PayloadLive] FASE 10 - OUTPUT FINALE - result completo:',
      result
    );

    return result;
  },

  // Aggiorna il livescore degli eventi quando arrivano aggiornamenti dal socket
  updateLiveEventLivescoreFromSocket(
    { state, commit }: any,
    payload: Array<{ id: string | number; livescore: any[] }>
  ) {
    console.log(
      '[IllinoisSport Livescore] Action chiamata con payload:',
      payload
    );

    if (!Array.isArray(payload) || !payload.length) {
      console.log('[IllinoisSport Livescore] Payload non valido o vuoto');
      return;
    }

    console.log(
      '[IllinoisSport Livescore] Processando',
      payload.length,
      'cambiamenti'
    );

    for (const change of payload) {
      const eventId = String(change.id);
      const livescoreArray = Array.isArray(change.livescore)
        ? change.livescore
        : [];

      console.log(
        '[IllinoisSport Livescore] Evento',
        eventId,
        '- livescore array length:',
        livescoreArray.length
      );

      if (!livescoreArray.length) {
        console.log(
          '[IllinoisSport Livescore] Evento',
          eventId,
          '- livescore array vuoto, skip'
        );
        continue;
      }

      // Prendi l'ultimo livescore come fa odds.module.js
      const latestLivescore = livescoreArray[livescoreArray.length - 1];

      // Verifica che l'evento esista nello store
      const existing = state.liveEventById?.[eventId];
      if (!existing || !existing.event) {
        console.log(
          '[IllinoisSport Livescore] Evento',
          eventId,
          '- non trovato nello store liveEventById'
        );
        continue;
      }

      // Verifica che il nuovo livescore sia più recente (stesso controllo di odds)
      const currentTime = Number(
        existing.event.livescore?.Scoreboard?.Time || 0
      );
      const newTime = Number(latestLivescore?.Scoreboard?.Time || 0);

      console.log(
        '[IllinoisSport Livescore] Evento',
        eventId,
        '- tempo corrente:',
        currentTime,
        '- nuovo tempo:',
        newTime
      );

      if (newTime >= currentTime) {
        console.log(
          '[IllinoisSport Livescore] Evento',
          eventId,
          '- aggiornamento livescore (nuovo >= corrente)'
        );
        commit('updateLiveEventLivescore', {
          eventId,
          livescore: latestLivescore
        });

        // Aggiorna anche l'evento dentro liveEventsBySport per LiveTrackerIllinois.vue
        commit('updateLiveEventLivescoreInGroupedEvents', {
          eventId,
          livescore: latestLivescore
        });
      } else {
        console.log(
          '[IllinoisSport Livescore] Evento',
          eventId,
          '- skip aggiornamento (nuovo < corrente)'
        );
      }
    }

    console.log('[IllinoisSport Livescore] Action completata');
  },
  // Actions per gestione errori
  setBetError(
    { commit }: any,
    payload: { response: BetErrorResponse; betType: 'place' | 'book' }
  ) {
    const { response, betType } = payload;
    const { errorCode, errorReason } = translateBetError(response, betType);
    const betErrorState: BetErrorState = {
      response,
      betType,
      errorCode,
      errorReason
    };
    commit('SET_BET_ERROR', betErrorState);
  },
  clearBetError({ commit }: any) {
    commit('CLEAR_BET_ERROR');
  },
  setCartValidationErrors({ commit }: any, payload: CartValidationError[]) {
    commit('SET_CART_VALIDATION_ERRORS', payload || []);
  },
  clearCartValidationErrors({ commit }: any) {
    commit('CLEAR_CART_VALIDATION_ERRORS');
  },
  // Actions per BetOutcome (gestione centralizzata outcome scommesse)
  setBetOutcomeSuccess(
    { commit }: any,
    payload: { bet_code: string; short_code: string }
  ) {
    commit('SET_BET_OUTCOME', {
      open: true,
      type: 'success',
      outcome: {
        bet_code: payload.bet_code,
        short_code: payload.short_code,
        result: true
      } as SuccessBetOutcomeModal
    });
  },
  setBetOutcomeBooked({ commit }: any, payload: { booked_code: string }) {
    commit('SET_BET_OUTCOME', {
      open: true,
      type: 'booked',
      outcome: {
        booked_code: payload.booked_code,
        result: true
      } as BookedBetOutcomeModal
    });
  },
  setBetOutcomeError(
    { commit }: any,
    payload: {
      response: BetErrorResponse;
      betType: 'place' | 'book';
      oddsToUpdate?: OddsToUpdate[];
    }
  ) {
    const { errorCode, errorReason } = translateBetError(
      payload.response,
      payload.betType
    );
    commit('SET_BET_OUTCOME', {
      open: true,
      type: 'error',
      outcome: {
        error_code: errorCode,
        reason: errorReason,
        result: false
      } as ErrorBetOutcomeModal,
      oddsToUpdate: payload.oddsToUpdate
    });
  },
  setOddsToUpdate({ commit }: any, payload: { oddsToUpdate: OddsToUpdate[] }) {
    commit('SET_ODDS_TO_UPDATE', payload.oddsToUpdate);
  },
  clearOddsToUpdate({ commit }: any) {
    commit('CLEAR_ODDS_TO_UPDATE');
  },
  setBetOutcomeReview({ commit }: any, payload: { review_code: string }) {
    commit('SET_BET_OUTCOME', {
      open: true,
      type: 'review',
      outcome: {
        review_code: payload.review_code,
        review_short_code: payload.review_code,
        result: true
      } as ReviewBetOutcomeModal
    });
  },
  clearBetOutcome({ commit }: any) {
    commit('CLEAR_BET_OUTCOME');
  },
  // Actions per persistenza stato navigazione
  saveTournamentMarketState(
    { commit }: any,
    payload: {
      tournamentId: string;
      marketGroup: string | null;
      market: string | null;
    }
  ) {
    commit('SET_TOURNAMENT_MARKET_STATE', payload);
  },
  saveLiveSportState({ commit }: any, payload: string | null) {
    commit('SET_LIVE_SPORT_STATE', payload);
  },
  setShowCart({ commit }: any, value: boolean) {
    commit('SET_SHOW_CART', value);
  },
  setOpenMyBetModal({ commit }: any, value: boolean) {
    commit('SET_OPEN_MY_BET_MODAL', value);
  },
  setOpenSportTree({ commit }: any, value: boolean) {
    commit('SET_OPEN_SPORT_TREE', value);
  }
};

// Helper function per tracciare un outcome aggiornato
function markOutcomeAsUpdatedHelper(state: SportState, uniqueId: string) {
  // Registra il timestamp dell'outcome aggiornato
  state.recentlyUpdatedOutcomes = {
    ...state.recentlyUpdatedOutcomes,
    [uniqueId]: Date.now()
  };

  // Programma il cleanup dopo 1 secondo
  setTimeout(() => {
    const updated = { ...state.recentlyUpdatedOutcomes };
    delete updated[uniqueId];
    state.recentlyUpdatedOutcomes = updated;
  }, 1000);
}

// Helper function per tradurre error_code in chiavi i18n
function translateBetError(
  response: BetErrorResponse,
  betType: 'place' | 'book'
): { errorCode: string; errorReason: string } {
  if (!response) {
    return { errorCode: '', errorReason: 'n.d.' };
  }

  if (response.message === 'User Not authorized!!!') {
    return {
      errorCode: 'bet_not_accepted',
      errorReason: 'alertlogin'
    };
  }

  if (!response.error_code) {
    return { errorCode: '', errorReason: 'n.d.' };
  }

  let errorCode = '';
  let errorReason = '';

  switch (response.error_code) {
    case 'totals_incorrect':
    case 'vincols_check':
      errorCode = 'bet_not_accepted';
      errorReason = 'pleaseTryAgain';
      break;
    case 'vincols_repetition':
      errorCode = 'bet_not_accepted';
      errorReason = 'vincols_repetitions';
      break;
    case 'external_save_failed':
      if (response.reason === 'Errore range di quote non rispettate') {
        errorCode = 'limitExceed';
        errorReason = response.reason;
      } else if (response.reason === 'reject by Ai') {
        errorCode = 'bet_not_accepted';
        errorReason = 'rejectedByAi';
      } else {
        errorCode = 'bet_not_accepted';
        errorReason = response.reason || 'pleaseTryAgain';
      }
      break;
    case 'review':
      errorCode = 'betReview';
      errorReason = 'betUnderReview';
      break;
    case 'event_not_avaiable':
      errorCode = 'bet_not_accepted';
      errorReason = 'oddNotAvailableInCart';
      break;
    case 'user_locked':
      errorCode = 'user_locked';
      errorReason = 'errors.user_locked_text';
      break;
    case 'agency_locked':
      errorCode = 'agency_locked';
      errorReason = 'agency_locked';
      break;
    case 'MAX_CURRENT_WEEK_PLAYABLE':
      errorCode = 'errors.MAX_CURRENT_WEEK_PLAYABLE';
      errorReason = 'pleaseTryAgain';
      break;
    case 'MAX_CURRENT_DAY_PLAYABLE':
      errorCode = 'errors.MAX_CURRENT_DAY_PLAYABLE';
      errorReason = 'pleaseTryAgain';
      break;
    case 'user_profile_forbidden':
      errorCode = 'errors.userProfileNotEnabled';
      errorReason = 'pleaseTryAgain';
      break;
    case 'INVALID_CURRENCY':
      errorCode = 'errors.systemError';
      errorReason = 'pleaseTryAgain';
      break;
    case 'reject_bet_time_live_odd_expired':
      errorCode = 'bet_not_accepted';
      errorReason = 'marketClosedPleaseTryAgain';
      break;
    case 'user_staged':
      errorCode = 'bet_not_accepted';
      errorReason = 'errors.user_staged_text';
      break;
    case 'cant_book_live_odds':
      errorCode = 'cant_book_live_odds';
      errorReason = 'pleaseTryAgain';
      break;
    case 'BET_NOT_ALLOWED':
      errorCode = 'bet_not_allowed';
      errorReason = 'oddNotAvailableInCart';
      break;
    default:
      errorCode = 'errors.systemError';
      errorReason = 'pleaseTryAgain';
      break;
  }

  return { errorCode, errorReason };
}

const mutations = {
  setStatsIframesRulesMap(
    state: SportState & {
      statsIframesRulesMap?: Record<string, StatsIframeRule>;
    },
    payload: Record<string, StatsIframeRule>
  ) {
    (state as any).statsIframesRulesMap = payload || {};
  },
  setSports(state: SportState, sports: Sport[]) {
    state.sports = sports;
  },

  // Aggiorna outcomes live partendo da payload di odds.set
  updateLiveOutcomesFromFeed(
    state: SportState,
    payload: LiveOddsUpdatePayloadItem[]
  ) {
    console.log('ENTERTO  IN updateLiveOutcomesFromFeed');
    console.log('!Array.isArray(payload)', !Array.isArray(payload));
    if (!Array.isArray(payload) || !payload.length) return;

    const liveMap = state.liveEventById || {};

    for (const item of payload) {
      const eventKey = String(item.id);
      const entry = (liveMap as any)[eventKey];
      if (!entry || !entry.event || !entry.event.odds) {
        // Evento non trovato o privo di odds nel liveEventById
        // eslint-disable-next-line no-console

        continue;
      }
      // eslint-disable-next-line no-console
      //console.log(`[live feed] evento ${eventKey} trovato`);

      const sets: LiveOddsSetItem[] =
        item && item.odds && Array.isArray(item.odds.set) ? item.odds.set : [];
      if (!sets.length) {
        // eslint-disable-next-line no-console
        //console.log(`[live feed] evento ${eventKey}: nessun update in odds.set`);
        continue;
      }

      // Partiamo dalla mappa odds corrente e ricostruiamola immutabilmente
      let workingOdds: LiveEventOddsNormalized =
        (entry.event.odds as LiveEventOddsNormalized) || ({} as any);

      for (const s of sets) {
        const compositeUnique = String(s.unique_id || '');
        const marketUnique =
          extractMarketUniqueIdFromComposite(compositeUnique);
        if (!marketUnique) {
          // eslint-disable-next-line no-console
          //console.log( `[live feed] evento ${eventKey}: unique_id ${compositeUnique} non valido (marketUnique assente)`);
          continue;
        }

        const market = workingOdds[marketUnique];
        if (!market || !market.outcome) {
          // eslint-disable-next-line no-console
          //console.log(  `[live feed] evento ${eventKey}: market ${marketUnique} NON trovato` );
          continue;
        }
        // eslint-disable-next-line no-console
        //console.log( `[live feed] evento ${eventKey}: market ${marketUnique} trovato` );

        const outcomeMap = market.outcome as Record<
          string,
          PreviewOutcomeNormalized
        >;
        const targetOutcome = outcomeMap[compositeUnique];
        if (!targetOutcome) {
          // eslint-disable-next-line no-console
          //console.log( `[live feed] evento ${eventKey}: outcome ${compositeUnique} NON trovato` );
          continue;
        }
        // eslint-disable-next-line no-console
        //console.log( `[live feed] evento ${eventKey}: outcome ${compositeUnique} trovato` );

        const attrs = s._attributes || {};
        // Costruisci una copia immutabile dell'outcome per garantire reattività
        let updatedOutcome: any = { ...targetOutcome, type: 'live' };
        // Aggiorno i valori noti
        if (attrs.odds != null) {
          const prevOddValue =
            (updatedOutcome as any).outcome_odd_value != null
              ? (updatedOutcome as any).outcome_odd_value
              : (updatedOutcome as any).value;
          const nextOddValue = String(attrs.odds);
          // eslint-disable-next-line no-console
          //console.log( `[live feed] evento ${eventKey} market ${marketUnique} outcome ${compositeUnique}: odd ${prevOddValue} -> ${nextOddValue}` );
          updatedOutcome.outcome_odd_value = nextOddValue;
          updatedOutcome.outcome_odd_value_for_order = nextOddValue;
          // Supporta struttura alternativa che usa "value"
          updatedOutcome.value = nextOddValue;
        }
        if (attrs.status != null) {
          const prevStatus = (updatedOutcome as any).status;
          const nextStatus = Number(attrs.status);
          // eslint-disable-next-line no-console
          //console.log( `[live feed] evento ${eventKey} market ${marketUnique} outcome ${compositeUnique}: status ${prevStatus} -> ${nextStatus}` );
          updatedOutcome.status = nextStatus;
        }
        if (attrs.lastUpdate != null) {
          const prevLastUpdate = (updatedOutcome as any).lastUpdate;
          const nextLastUpdate = String(attrs.lastUpdate);
          // eslint-disable-next-line no-console
          //console.log( `[live feed] evento ${eventKey} market ${marketUnique} outcome ${compositeUnique}: lastUpdate ${prevLastUpdate} -> ${nextLastUpdate}` );
          updatedOutcome.lastUpdate = nextLastUpdate;
        }
        if (
          (attrs as any).ext_id != null ||
          (attrs as any).external_id != null
        ) {
          // Normalizza il nome proprietà usato nell'app (ext_id)
          (updatedOutcome as any).ext_id = String(
            (attrs as any).ext_id || (attrs as any).external_id
          );
        }

        // Ricostruisci outcome, market e odds immutabilmente
        const newOutcomeMap = {
          ...outcomeMap,
          [compositeUnique]: updatedOutcome
        } as Record<string, PreviewOutcomeNormalized> as any;
        const newMarket = {
          ...market,
          outcome: newOutcomeMap
        } as typeof market;
        workingOdds = {
          ...workingOdds,
          [marketUnique]: newMarket
        } as LiveEventOddsNormalized;

        // Traccia l'outcome aggiornato per evidenziarlo nel componente
        markOutcomeAsUpdatedHelper(state, compositeUnique);
      }

      // Reassembla stato immutabile per Vuex reattività (entry, event, odds)
      state.liveEventById = {
        ...state.liveEventById,
        [eventKey]: {
          ...entry,
          event: {
            ...entry.event,
            odds: workingOdds
          }
        }
      } as any;
    }
  },
  markOutcomeAsUpdated(state: SportState, uniqueId: string) {
    markOutcomeAsUpdatedHelper(state, uniqueId);
  },
  // Imposta il flag per mostrare il messaggio di aggiornamento quote
  setShowOddsUpdateMessage(
    state: SportState & { showOddsUpdateMessage?: boolean },
    value: boolean
  ) {
    // @ts-ignore aggiunta dinamica su SportState
    state.showOddsUpdateMessage = value;
  },
  setSportsAntepost(state: SportState, sportsAntepost: Sport[]) {
    state.sportsAntepost = sportsAntepost;
  },
  setSportLive(state: SportState, payload) {
    state.sportLive = payload;
  },
  setTournaments(state: SportState, tournaments: FeaturedTournament[]) {
    state.tournaments = tournaments;
  },
  setTournamentsForCategory(
    state: SportState,
    payload: { categoryId: string; tournaments: Tournament[] }
  ) {
    state.tournamentsByCategory = {
      ...state.tournamentsByCategory,
      [payload.categoryId]: payload.tournaments
    } as Record<string, Tournament[]>;
  },
  setFeaturedTournamentsBySport(
    state: SportState,
    payload: { sportId: string; tournaments: FeaturedTournament[] }
  ) {
    state.featuredTournamnetsBySport = {
      ...state.featuredTournamnetsBySport,
      [payload.sportId]: payload.tournaments
    } as Record<string, FeaturedTournament[]>;
  },
  setCategories(state: SportState, categories: CategoriesBySport) {
    state.categories = categories;
  },
  setCategoriesForSport(
    state: SportState,
    payload: { sportId: string; categories: Category[] }
  ) {
    console.log(
      'setCategoriesForSport',
      payload.sportId.toString(),
      payload.categories
    );
    state.categories = {
      ...state.categories,
      [payload.sportId]: payload.categories
    } as CategoriesBySport;
  },
  setLoading(state: SportState, loading: boolean) {
    state.loading = loading;
  },
  setLoadingLive(state: SportState, loading: boolean) {
    state.loadingLive = loading;
  },
  setLoadingTournaments(state: SportState, loading: boolean) {
    state.loadingTournaments = loading;
  },
  setLoadingCategories(state: SportState, loading: boolean) {
    state.loadingCategories = loading;
  },
  setError(state: SportState, error: string | null) {
    state.error = error;
  },
  setTournamentsError(state: SportState, error: string | null) {
    state.tournamentsError = error;
  },
  setCategoriesError(state: SportState, error: string | null) {
    state.categoriesError = error;
  },
  setFieldStatistics(
    state: SportState,
    payload: Partial<Pick<FieldStatisticsState, 'sportId' | 'eventId'>>
  ) {
    state.fieldStatistics = {
      sportId:
        payload.sportId !== undefined
          ? payload.sportId
          : state.fieldStatistics?.sportId ?? null,
      eventId:
        payload.eventId !== undefined
          ? payload.eventId
          : state.fieldStatistics?.eventId ?? null
    } as FieldStatisticsState;
  },
  setFieldStatisticsSportId(
    state: SportState,
    sportId: string | number | null
  ) {
    state.fieldStatistics = {
      ...(state.fieldStatistics || { sportId: null, eventId: null }),
      sportId
    } as FieldStatisticsState;
  },
  setFieldStatisticsEventId(
    state: SportState,
    eventId: string | number | null
  ) {
    state.fieldStatistics = {
      ...(state.fieldStatistics || { sportId: null, eventId: null }),
      eventId
    } as FieldStatisticsState;
  },
  setActiveLiveSport(state: SportState, sportId: string | number | null) {
    state.activeLiveSport = sportId;
  },
  setLiveSportsAvailable(
    state: SportState,
    payload: import('../../../types/sport').LiveEventsSportAvailable[]
  ) {
    state.liveSportsAvailable = payload;
  },
  setLiveEventsBySport(
    state: SportState,
    payload: {
      sportId: string;
      data: import('../../../types/sport').LiveEventsCountResult;
    }
  ) {
    state.liveEventsBySport = {
      ...state.liveEventsBySport,
      [payload.sportId]: payload.data
    } as Record<string, import('../../../types/sport').LiveEventsCountResult>;
  },
  setEventById(
    state: SportState,
    payload: { eventId: string; event: EventByID }
  ) {
    // Garantisce che qualunque odds già presenti siano marcate come prematch
    const withType: EventByID = {
      ...payload.event,
      event: payload.event?.event
        ? ({
            ...(payload.event.event as any),
            odds: addTypeToPreviewOddArray(
              (payload.event.event as any).odds as PreviewOdd[],
              'prematch'
            )
          } as any)
        : (payload.event?.event as any),
      oddsByMarketGroup: Object.keys(
        payload.event?.oddsByMarketGroup || {}
      ).reduce((acc: any, groupId: string) => {
        acc[groupId] = addTypeToPreviewOddArray(
          (payload.event.oddsByMarketGroup as any)[groupId] as PreviewOdd[],
          'prematch'
        );
        return acc;
      }, {})
    } as EventByID;

    state.eventsById = {
      ...state.eventsById,
      [payload.eventId]: withType
    } as Record<string, EventByID>;
  },
  setEventOddsByMarketGroup(
    state: SportState,
    payload: {
      eventId: string;
      marketGroupId: string;
      odds: PreviewOdd[];
    }
  ) {
    const current = state.eventsById[payload.eventId];
    if (!current) return;

    state.eventsById = {
      ...state.eventsById,
      [payload.eventId]: {
        ...current,
        oddsByMarketGroup: {
          ...current.oddsByMarketGroup,
          [payload.marketGroupId]: addTypeToPreviewOddArray(
            payload.odds,
            'prematch'
          )
        }
      }
    } as Record<string, EventByID>;
  },
  setEventsByTournament(
    state: SportState,
    payload: { tournamentId: string; events: EventDetailsEvent[] }
  ) {
    state.eventsByTournament = {
      ...state.eventsByTournament,
      [payload.tournamentId]: payload.events
    } as Record<string, EventDetailsEvent[]>;
  },
  setPreviewByTournament(
    state: SportState,
    payload: { tournamentId: string; events: EventDetailsEvent[] }
  ) {
    state.previewByTournament = {
      ...state.previewByTournament,
      [payload.tournamentId]: payload.events
    } as Record<string, EventDetailsEvent[]>;
  },

  setHeaderSignByTournament(
    state: SportState,
    payload: {
      tournamentId: string;
      headerSign: import('../../../types/sport').HeaderSign[];
    }
  ) {
    state.headerSignByTournament = {
      ...state.headerSignByTournament,
      [payload.tournamentId]: payload.headerSign
    } as Record<string, import('../../../types/sport').HeaderSign[]>;
  },

  setMarketGroupsForTournament(
    state: SportState,
    payload: { tournamentId: string; groups: MarketGroup[] }
  ) {
    state.marketsByTournament = {
      ...state.marketsByTournament,
      [payload.tournamentId]: payload.groups
    } as Record<string, MarketGroup[]>;
  },

  setLiveEventById(
    state: SportState,
    payload: {
      eventId: string;
      data: import('../../../types/sport').LiveEventByID;
    }
  ) {
    state.liveEventById = {
      ...state.liveEventById,
      [payload.eventId]: payload.data
    } as Record<string, import('../../../types/sport').LiveEventByID>;
  },

  // Sostituisce l'intero oggetto event mantenendo gli altri campi dell'entry
  setLiveEventEntity(
    state: SportState,
    payload: {
      eventId: string;
      event: import('../../../types/sport').LiveEventDetailsEvent | any;
    }
  ) {
    const current = state.liveEventById[payload.eventId];
    if (!current) return;

    state.liveEventById = {
      ...state.liveEventById,
      [payload.eventId]: {
        ...current,
        event: payload.event
      }
    } as Record<string, import('../../../types/sport').LiveEventByID>;
  },

  // Aggiorna esclusivamente i marketsGroups dell'evento live
  setLiveEventMarketsGroups(
    state: SportState,
    payload: {
      eventId: string;
      marketsGroups:
        | import('../../../types/sport').EventDetailsMarketsGroup[]
        | any[];
    }
  ) {
    const current = state.liveEventById[payload.eventId];
    if (!current) return;

    state.liveEventById = {
      ...state.liveEventById,
      [payload.eventId]: {
        ...current,
        marketsGroups: payload.marketsGroups
      }
    } as Record<string, import('../../../types/sport').LiveEventByID>;
  },

  setLiveEventOddsByMarketGroup(
    state: SportState,
    payload: {
      eventId: string;
      marketGroupId: string;
      odds: import('../../../types/sport').PreviewOdd[];
    }
  ) {
    const current = state.liveEventById[payload.eventId];
    if (!current) return;

    state.liveEventById = {
      ...state.liveEventById,
      [payload.eventId]: {
        ...current,
        oddsByMarketGroup: {
          ...(current.oddsByMarketGroup || {}),
          [payload.marketGroupId]: payload.odds
        }
      }
    } as Record<string, import('../../../types/sport').LiveEventByID>;
  },

  // Nuova mutation: sovrascrive direttamente le odds dentro l'evento live corrente
  setLiveEventOddsInEvent(
    state: SportState,
    payload: {
      eventId: string;
      odds: import('../../../types/sport').PreviewOdd[];
    }
  ) {
    const current = state.liveEventById[payload.eventId];
    if (!current) return;

    state.liveEventById = {
      ...state.liveEventById,
      [payload.eventId]: {
        ...current,
        event: {
          ...(current.event as any),
          odds: payload.odds as unknown as any
        }
      }
    } as Record<string, import('../../../types/sport').LiveEventByID>;
  },

  mergeTournamentMarketOdds(
    state: SportState,
    payload: { tournamentId: string; responseEvents: PreviewEventGroup[] }
  ) {
    const tournamentKey = payload.tournamentId.toString();
    // Usa sempre eventsByTournament come sorgente per i merge di market
    const sourceGroups = state.eventsByTournament[tournamentKey] || [];
    const existingGroups = sourceGroups as unknown as PreviewEventGroup[];

    // Utility per confrontare solo la data (YYYY-M-D) ignorando l'orario
    const getDateKey = (dateString: string) => {
      const d = new Date(dateString);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    };

    const updatedGroups: PreviewEventGroup[] = existingGroups.map(group => ({
      scheduled: group.scheduled,
      events: group.events ? [...group.events] : []
    }));

    for (const responseGroup of payload.responseEvents) {
      const responseDateKey = getDateKey(responseGroup.scheduled);
      let targetGroupIndex = updatedGroups.findIndex(g => {
        return getDateKey(g.scheduled) === responseDateKey;
      });

      if (targetGroupIndex === -1) {
        // Se il gruppo non esiste, aggiungilo così com'è
        updatedGroups.push({
          scheduled: responseGroup.scheduled,
          events: responseGroup.events ? [...responseGroup.events] : []
        });
        continue;
      }

      // Merge degli eventi dentro il gruppo esistente
      const targetGroup = updatedGroups[targetGroupIndex];
      const newEventsArray: PreviewEvent[] = [...targetGroup.events];

      for (const responseEvent of responseGroup.events) {
        const existingEventIndex = newEventsArray.findIndex(
          e => e.id === responseEvent.id
        );

        if (existingEventIndex === -1) {
          newEventsArray.push({ ...responseEvent });
          continue;
        }

        // Merge delle odds: sostituisci se stessa marketName+specifier, altrimenti aggiungi
        const existingEvent = newEventsArray[existingEventIndex];
        const mergedOdds: PreviewOdd[] = existingEvent.odds
          ? [...existingEvent.odds]
          : [];

        for (const newOdd of responseEvent.odds || []) {
          const idx = mergedOdds.findIndex(
            o =>
              o.marketName === newOdd.marketName &&
              o.specifier === newOdd.specifier
          );
          if (idx !== -1) {
            mergedOdds[idx] = newOdd;
          } else {
            mergedOdds.push(newOdd);
          }
        }

        newEventsArray[existingEventIndex] = {
          ...existingEvent,
          odds: mergedOdds
        } as PreviewEvent;
      }

      updatedGroups[targetGroupIndex] = {
        scheduled: targetGroup.scheduled,
        events: newEventsArray
      };
    }

    // Aggiorna sempre eventsByTournament
    state.eventsByTournament = {
      ...state.eventsByTournament,
      [tournamentKey]: updatedGroups as unknown as EventDetailsEvent[]
    } as Record<string, EventDetailsEvent[]>;
  },

  // Aggiorna solo il campo livescore dell'evento senza sovrascrivere l'intero oggetto
  updateLiveEventLivescore(
    state: SportState,
    payload: {
      eventId: string;
      livescore: import('../../../types/sport').LivescoreData;
    }
  ) {
    console.log(
      '[IllinoisSport Livescore] Mutation chiamata per evento',
      payload.eventId
    );

    const current = state.liveEventById[payload.eventId];
    if (!current || !current.event) {
      console.log(
        '[IllinoisSport Livescore] Mutation - evento',
        payload.eventId,
        'non trovato nello store'
      );
      return;
    }

    const oldTime = current.event.livescore?.Scoreboard?.Time;
    const newTime = payload.livescore?.Scoreboard?.Time;
    console.log(
      '[IllinoisSport Livescore] Mutation - evento',
      payload.eventId,
      '- aggiornando livescore da tempo',
      oldTime,
      'a',
      newTime
    );

    state.liveEventById = {
      ...state.liveEventById,
      [payload.eventId]: {
        ...current,
        event: {
          ...current.event,
          livescore: payload.livescore
        }
      }
    } as Record<string, import('../../../types/sport').LiveEventByID>;

    console.log(
      '[IllinoisSport Livescore] Mutation - evento',
      payload.eventId,
      '- livescore aggiornato con successo'
    );
  },

  // Aggiorna il livescore dell'evento dentro liveEventsBySport.groupedEvents
  updateLiveEventLivescoreInGroupedEvents(
    state: SportState,
    payload: {
      eventId: string;
      livescore: import('../../../types/sport').LivescoreData;
    }
  ) {
    console.log(
      '[IllinoisSport Livescore] Mutation groupedEvents - cercando evento',
      payload.eventId
    );

    let eventFound = false;

    // Itera su tutti gli sport in liveEventsBySport
    for (const sportId in state.liveEventsBySport) {
      const sportData = state.liveEventsBySport[sportId];
      if (!sportData || !Array.isArray(sportData.groupedEvents)) continue;

      // Itera su tutti i gruppi di eventi
      for (
        let groupIndex = 0;
        groupIndex < sportData.groupedEvents.length;
        groupIndex++
      ) {
        const group = sportData.groupedEvents[groupIndex];
        if (!Array.isArray(group)) continue;

        // Cerca l'evento per ID nel gruppo
        const eventIndex = group.findIndex(
          (event: any) => String(event?.id) === payload.eventId
        );

        if (eventIndex !== -1) {
          console.log(
            '[IllinoisSport Livescore] Mutation groupedEvents - evento',
            payload.eventId,
            'trovato nello sport',
            sportId,
            'gruppo',
            groupIndex,
            'indice',
            eventIndex
          );

          // Aggiorna il livescore dell'evento creando una nuova reference
          const updatedGroup = [...group];
          updatedGroup[eventIndex] = {
            ...updatedGroup[eventIndex],
            livescore: payload.livescore
          };

          // Aggiorna il gruppo nell'array groupedEvents creando una nuova reference
          const updatedGroupedEvents = [...sportData.groupedEvents];
          updatedGroupedEvents[groupIndex] = updatedGroup;

          // Aggiorna lo stato immutabilmente creando nuove references per tutti i livelli
          state.liveEventsBySport = {
            ...state.liveEventsBySport,
            [sportId]: {
              ...sportData,
              groupedEvents: updatedGroupedEvents
            }
          } as Record<
            string,
            import('../../../types/sport').LiveEventsCountResult
          >;

          console.log(
            '[IllinoisSport Livescore] Mutation groupedEvents - evento',
            payload.eventId,
            'aggiornato con successo nello sport',
            sportId
          );

          eventFound = true;
          // Non fare return qui, potrebbe esserci lo stesso evento in più sport
        }
      }
    }

    if (!eventFound) {
      console.log(
        '[IllinoisSport Livescore] Mutation groupedEvents - evento',
        payload.eventId,
        'non trovato in liveEventsBySport'
      );
    }
  },
  // Mutations per gestione errori
  SET_BET_ERROR(
    state: SportState & { betError?: BetErrorState | null },
    payload: BetErrorState
  ) {
    (state as any).betError = payload;
  },
  CLEAR_BET_ERROR(state: SportState & { betError?: BetErrorState | null }) {
    (state as any).betError = null;
  },
  SET_CART_VALIDATION_ERRORS(
    state: SportState & { cartValidationErrors?: CartValidationError[] },
    payload: CartValidationError[]
  ) {
    (state as any).cartValidationErrors = payload || [];
  },
  CLEAR_CART_VALIDATION_ERRORS(
    state: SportState & { cartValidationErrors?: CartValidationError[] }
  ) {
    (state as any).cartValidationErrors = [];
  },
  // Mutations per persistenza stato navigazione
  SET_TOURNAMENT_MARKET_STATE(
    state: SportState & {
      savedTournamentMarketState?: Record<
        string,
        { marketGroup: string | null; market: string | null }
      >;
    },
    payload: {
      tournamentId: string;
      marketGroup: string | null;
      market: string | null;
    }
  ) {
    (state as any).savedTournamentMarketState = {
      ...((state as any).savedTournamentMarketState || {}),
      [payload.tournamentId]: {
        marketGroup: payload.marketGroup,
        market: payload.market
      }
    };
  },
  SET_LIVE_SPORT_STATE(
    state: SportState & { savedLiveSportState?: string | null },
    payload: string | null
  ) {
    (state as any).savedLiveSportState = payload;
  },
  SET_BET_OUTCOME(
    state: SportState,
    payload: {
      open: boolean;
      type: 'success' | 'booked' | 'error' | 'review';
      outcome:
        | SuccessBetOutcomeModal
        | BookedBetOutcomeModal
        | ErrorBetOutcomeModal
        | ReviewBetOutcomeModal
        | null;
      oddsToUpdate?: OddsToUpdate[];
    }
  ) {
    state.betOutcome = {
      open: payload.open,
      type: payload.type,
      outcome: payload.outcome,
      oddsToUpdate: payload.oddsToUpdate
    };
  },
  CLEAR_BET_OUTCOME(state: SportState) {
    state.betOutcome = {
      open: false,
      type: 'error',
      outcome: null
    };
  },
  SET_ODDS_TO_UPDATE(state: SportState, payload: OddsToUpdate[]) {
    state.betOutcome = {
      ...state.betOutcome,
      oddsToUpdate: payload
    };
  },
  CLEAR_ODDS_TO_UPDATE(state: SportState) {
    if (state.betOutcome) {
      state.betOutcome.oddsToUpdate = [];
    }
  },
  SET_SHOW_CART(state: SportState, value: boolean) {
    (state as any).showCart = value;
  },
  SET_OPEN_MY_BET_MODAL(state: SportState, value: boolean) {
    (state as any).openMyBetModal = value;
  },
  SET_OPEN_SPORT_TREE(state: SportState, value: boolean) {
    (state as any).openSportTree = value;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
